import { canDeviceWrite, canonicalRouteTemplateWorkType, canonicalJsonStringify, compareDecimalCursors, compareInstants, compareRouteUseOrder, datasetEpochSchema, decimalCursorSchema, deviceCursorSchema, evaluateTombstonePurge, EXPANDED_ROUTE_DRAFT_WIRE_GENERATION, exportPayloadSchema, instantSchema, isEmptyRouteDraftSnapshotForProductionA6Rollback, immutableCheckpointSchema, jsonValueSchema, locationSchema, localProfileSchema, MAX_ROUTE_VIA_POINTS, normalizeRouteSeedForCurrentScoring, pendingSyncOperationSchema, parseShiftEditorDraft, productionA6RollbackRouteVariantSchema, productionA6RollbackShiftAggregateSchema, projectShiftAggregateForProductionA6Rollback, projectShiftEditorDraftForV3Rollback, revisionSchema, routeFingerprintSha256, routeDraftEndpointLocationSnapshot, ROUTE_DRAFT_WIRE_WRITE_MODE, routeDraftWireGenerationOf, routeTemplateSchema, routeVariantSchema, sha256HexUtf8, shiftAggregateSchema, syncEntityPayloadSchema, syncOperationSchema, tombstoneLifecycleSchema, tripRevisionSchema, uuidSchema, vehicleSchema, verifiedExportEvidenceSchema, shiftEditorDraftSchema, } from '@der-maschinfuehrer/contracts';
import { DatasetEpochMismatchError, DraftRevisionConflictError, FullResyncRequiredError, LocalReplacementCommittedError, ProfileAccessDeniedError, ProductionRollbackCompatibilityError, RepositoryClosedError, RevisionConflictError, TrashRestoreWindowExpiredError, UnsupportedStructuralMutationError, } from './errors.js';
import { derivePerformanceStats } from './stats.js';
import Dexie, {} from 'dexie';
import { putValidatedDeviceCursor } from './internal/device-cursor.js';
import { replacementExecutionImmutableFieldsMatch } from './internal/replacement-execution.js';
import { nextSnapshotMutationGeneration } from './internal/snapshot-generation.js';
import { entityRowFromPayload, entityTableKey, parseEntityRowEnvelope, parsePendingOperation, } from './internal/entity.js';
const TRASH_WINDOW_MILLISECONDS = 30 * 24 * 60 * 60 * 1_000;
const MAX_SHIFT_DRAFT_USER_DEFINED_LOCATIONS = MAX_ROUTE_VIA_POINTS + 3;
export class LocalDataStore {
    database;
    directory;
    directoryEntry;
    profileValue;
    runtime;
    #closed = false;
    constructor(database, directory, directoryEntry, profileValue, runtime) {
        this.database = database;
        this.directory = directory;
        this.directoryEntry = directoryEntry;
        this.profileValue = profileValue;
        this.runtime = runtime;
    }
    get local_profile_id() {
        return this.profileValue.local_profile_id;
    }
    get logical_database_name() {
        return this.profileValue.database_binding.database_name;
    }
    async profile() {
        this.assertOpen();
        return this.readConsistently([], async (storedProfile) => structuredClone(storedProfile));
    }
    async createExportPayload() {
        this.assertOpen();
        return this.readConsistently([
            this.database.entities,
            this.database.outbox,
            this.database.drafts,
            this.database.device_cursors,
            this.database.trip_revisions,
        ], async (storedProfile) => {
            if ((await this.database.drafts.count()) !== 0) {
                throw new Error('a complete export requires every autosave draft to be explicitly committed or discarded');
            }
            const runtime = await this.requireRuntimeState();
            const rows = (await this.database.entities.toArray())
                .map((row) => parseEntityRowEnvelope(row, storedProfile.dataset_epoch))
                .sort((left, right) => `${left.entity_type}\u0000${left.entity_id}`.localeCompare(`${right.entity_type}\u0000${right.entity_id}`));
            const entityPayloads = rows.map((row) => row.payload);
            const preferences = entityPayloads.filter((payload) => payload.entity_type === 'user_preferences');
            if (preferences.length !== 1 || preferences[0] === undefined) {
                throw new Error('a complete export requires exactly one user-preferences entity');
            }
            const pendingOutbox = (await this.database.outbox.toArray())
                .map((operation) => pendingSyncOperationSchema.parse(operation))
                .sort((left, right) => compareDecimalCursors(left.local_sequence, right.local_sequence) ||
                left.operation_id.localeCompare(right.operation_id));
            const activeCursorInput = await this.database.device_cursors.get(runtime.device_id);
            const activeCursor = activeCursorInput === undefined ? null : deviceCursorSchema.parse(activeCursorInput);
            const tripRevisions = (await this.database.trip_revisions.toArray())
                .map((revision) => tripRevisionSchema.parse(revision))
                .sort((left, right) => compareInstants(left.recorded_at, right.recorded_at) ||
                left.trip_revision_id.localeCompare(right.trip_revision_id));
            const payload = exportPayloadSchema.parse({
                dataset_epoch: currentDatasetEpoch(storedProfile, runtime),
                local_profile: structuredClone(storedProfile),
                shifts: entityPayloads.flatMap((entity) => entity.entity_type === 'shift_aggregate' ? [structuredClone(entity.value)] : []),
                trip_revisions: structuredClone(tripRevisions),
                vehicles: entityPayloads.flatMap((entity) => entity.entity_type === 'vehicle' ? [structuredClone(entity.value)] : []),
                route_variants: entityPayloads.flatMap((entity) => entity.entity_type === 'route_variant' ? [structuredClone(entity.value)] : []),
                route_templates: entityPayloads.flatMap((entity) => entity.entity_type === 'route_template' ? [structuredClone(entity.value)] : []),
                user_defined_locations: entityPayloads.flatMap((entity) => entity.entity_type === 'user_defined_location' ? [structuredClone(entity.value)] : []),
                location_merge_decisions: entityPayloads.flatMap((entity) => entity.entity_type === 'location_merge_decision' ? [structuredClone(entity.value)] : []),
                user_preferences: structuredClone(preferences[0].value),
                sync_metadata: {
                    pending_outbox: structuredClone(pendingOutbox),
                    device_cursor: activeCursor === null ? null : structuredClone(activeCursor),
                    last_server_cursor: activeCursor?.last_applied_cursor ?? null,
                },
            });
            validateTripRevisionReferences(payload.shifts, payload.trip_revisions);
            return {
                payload,
                covered_mutation_generation: decimalCursorSchema.parse(runtime.mutation_generation),
            };
        });
    }
    validateLocalReplacementSource(sourcePayloadInput) {
        this.assertOpen();
        parseLocalReplacementSourcePayload(sourcePayloadInput);
    }
    async getShiftAggregate(shiftIdInput) {
        this.assertOpen();
        const shiftId = uuidSchema.parse(shiftIdInput);
        return this.readConsistently([this.database.entities], async (storedProfile) => {
            const row = await this.database.entities.get(entityTableKey('shift_aggregate', shiftId));
            if (row?.payload.entity_type !== 'shift_aggregate') {
                return null;
            }
            assertRowEpoch(row, storedProfile.dataset_epoch);
            return structuredClone(row.payload.value);
        });
    }
    async listTripRevisions(shiftIdInput) {
        this.assertOpen();
        const shiftId = shiftIdInput === undefined ? undefined : uuidSchema.parse(shiftIdInput);
        return this.readConsistently([this.database.trip_revisions], async (storedProfile) => {
            const revisions = shiftId === undefined
                ? await this.database.trip_revisions.toArray()
                : await this.database.trip_revisions.where('shift_id').equals(shiftId).toArray();
            const stale = revisions.find((revision) => revision.dataset_epoch !== storedProfile.dataset_epoch);
            if (stale !== undefined) {
                throw new DatasetEpochMismatchError(storedProfile.dataset_epoch, stale.dataset_epoch);
            }
            return revisions
                .sort((left, right) => compareInstants(left.recorded_at, right.recorded_at) ||
                left.trip_revision_id.localeCompare(right.trip_revision_id))
                .map((revision) => structuredClone(revision));
        });
    }
    async listShiftAggregates(options = {}) {
        this.assertOpen();
        return this.readConsistently([this.database.entities], async (storedProfile) => {
            const rows = await this.database.entities
                .where('entity_type')
                .equals('shift_aggregate')
                .toArray();
            for (const row of rows) {
                assertRowEpoch(row, storedProfile.dataset_epoch);
            }
            return rows
                .filter((row) => row.payload.entity_type === 'shift_aggregate' &&
                (options.include_deleted === true ||
                    row.payload.value.shift.metadata.tombstone === null))
                .map((row) => structuredClone(row.payload.value));
        });
    }
    async getUserDefinedLocation(locationIdInput) {
        this.assertOpen();
        const locationId = uuidSchema.parse(locationIdInput);
        return this.readConsistently([this.database.entities], async (storedProfile) => {
            const row = await this.database.entities.get(entityTableKey('user_defined_location', locationId));
            if (row?.payload.entity_type !== 'user_defined_location') {
                return null;
            }
            assertRowEpoch(row, storedProfile.dataset_epoch);
            return structuredClone(row.payload.value);
        });
    }
    async listUserDefinedLocations(options = {}) {
        this.assertOpen();
        return this.readConsistently([this.database.entities], async (storedProfile) => {
            const rows = await this.database.entities
                .where('entity_type')
                .equals('user_defined_location')
                .toArray();
            for (const row of rows) {
                assertRowEpoch(row, storedProfile.dataset_epoch);
            }
            return rows
                .filter((row) => row.payload.entity_type === 'user_defined_location' &&
                (options.include_deleted === true || row.payload.value.metadata.tombstone === null))
                .sort((left, right) => left.entity_id.localeCompare(right.entity_id))
                .map((row) => structuredClone(row.payload.value));
        });
    }
    async getVehicle(vehicleIdInput) {
        this.assertOpen();
        const vehicleId = uuidSchema.parse(vehicleIdInput);
        return this.readConsistently([this.database.entities], async (storedProfile) => {
            const row = await this.database.entities.get(entityTableKey('vehicle', vehicleId));
            if (row?.payload.entity_type !== 'vehicle') {
                return null;
            }
            assertRowEpoch(row, storedProfile.dataset_epoch);
            return structuredClone(row.payload.value);
        });
    }
    async listVehicles(options = {}) {
        this.assertOpen();
        return this.readConsistently([this.database.entities], async (storedProfile) => {
            const rows = await this.database.entities.where('entity_type').equals('vehicle').toArray();
            for (const row of rows) {
                assertRowEpoch(row, storedProfile.dataset_epoch);
            }
            return rows
                .filter((row) => row.payload.entity_type === 'vehicle' &&
                (options.include_deleted === true || row.payload.value.metadata.tombstone === null))
                .sort((left, right) => left.entity_id.localeCompare(right.entity_id))
                .map((row) => structuredClone(row.payload.value));
        });
    }
    async getRouteVariant(routeVariantIdInput) {
        this.assertOpen();
        const routeVariantId = uuidSchema.parse(routeVariantIdInput);
        return this.readConsistently([this.database.entities], async (storedProfile) => {
            const row = await this.database.entities.get(entityTableKey('route_variant', routeVariantId));
            if (row?.payload.entity_type !== 'route_variant') {
                return null;
            }
            assertRowEpoch(row, storedProfile.dataset_epoch);
            return structuredClone(row.payload.value);
        });
    }
    async listRouteVariants(options = {}) {
        this.assertOpen();
        return this.readConsistently([this.database.entities], async (storedProfile) => {
            const rows = await this.database.entities
                .where('entity_type')
                .equals('route_variant')
                .toArray();
            for (const row of rows) {
                assertRowEpoch(row, storedProfile.dataset_epoch);
            }
            return rows
                .filter((row) => row.payload.entity_type === 'route_variant' &&
                (options.include_deleted === true || row.payload.value.metadata.tombstone === null))
                .sort((left, right) => left.entity_id.localeCompare(right.entity_id))
                .map((row) => structuredClone(row.payload.value));
        });
    }
    async saveRouteVariantPreferences(mutation) {
        this.assertOpen();
        const routeVariantId = uuidSchema.parse(mutation.route_variant_id);
        const expectedRevision = revisionSchema.parse(mutation.expected_revision);
        if (typeof mutation.favorite !== 'boolean') {
            throw new TypeError('route favorite preference must be boolean');
        }
        const operationId = uuidSchema.parse(this.runtime.uuid());
        const now = instantSchema.parse(this.runtime.now());
        const result = await this.database.transaction('rw', [
            this.database.entities,
            this.database.outbox,
            this.database.runtime_state,
            this.database.profiles,
            this.database.device_cursors,
        ], async () => {
            const storedProfile = await this.assertWriteAllowed();
            const runtime = await this.requireRuntimeState();
            const row = await this.database.entities.get(entityTableKey('route_variant', routeVariantId));
            if (row !== undefined) {
                assertRowEpoch(row, storedProfile.dataset_epoch);
            }
            const existing = row?.payload.entity_type === 'route_variant' ? row.payload.value : null;
            this.assertExpectedRevision(routeVariantId, expectedRevision, existing?.metadata.revision ?? null);
            if (existing === null) {
                throw new Error('route preference CAS accepted an absent route variant');
            }
            if (existing.metadata.tombstone !== null) {
                throw new UnsupportedStructuralMutationError('soft-deleted route variants require an explicit restore operation');
            }
            if (existing.favorite === mutation.favorite) {
                return { routeVariant: existing, profile: storedProfile };
            }
            const routeVariant = routeVariantForCurrentWriteMode({
                ...existing,
                favorite: mutation.favorite,
                metadata: nextExplicitEntityMetadata(existing, now),
            }, expandedRouteDraftWireActive(runtime));
            const payload = {
                entity_type: 'route_variant',
                value: routeVariant,
            };
            const operation = makePendingOperation({
                operation_id: operationId,
                device_id: runtime.device_id,
                local_profile_id: storedProfile.local_profile_id,
                dataset_epoch: runtime.dataset_epoch,
                local_sequence: runtime.next_local_sequence,
                operation_origin: 'local_edit',
                base_revision: existing.metadata.revision,
                payload,
                created_at: now,
            });
            await this.database.entities.put(entityRowFromPayload(payload, runtime.dataset_epoch));
            this.runtime.fault?.('after_domain_write');
            this.runtime.fault?.('before_outbox_write');
            await this.database.outbox.add(operation);
            await this.database.runtime_state.put({
                ...runtime,
                next_local_sequence: (BigInt(runtime.next_local_sequence) + 1n).toString(),
                mutation_generation: nextSnapshotMutationGeneration(runtime),
            });
            const updatedProfile = localProfileSchema.parse({
                ...storedProfile,
                last_mutation_at: now,
            });
            await this.database.profiles.put({ key: 'profile', value: updatedProfile });
            return { routeVariant, profile: updatedProfile };
        });
        this.profileValue = result.profile;
        return structuredClone(result.routeVariant);
    }
    async getRouteTemplate(routeTemplateIdInput) {
        this.assertOpen();
        const routeTemplateId = uuidSchema.parse(routeTemplateIdInput);
        return this.readConsistently([this.database.entities], async (storedProfile) => {
            const row = await this.database.entities.get(entityTableKey('route_template', routeTemplateId));
            if (row?.payload.entity_type !== 'route_template') {
                return null;
            }
            assertRowEpoch(row, storedProfile.dataset_epoch);
            if (row.payload.value.metadata.tombstone !== null) {
                return null;
            }
            return structuredClone(row.payload.value);
        });
    }
    async listRouteTemplates() {
        this.assertOpen();
        return this.readConsistently([this.database.entities], async (storedProfile) => {
            const rows = await this.database.entities
                .where('entity_type')
                .equals('route_template')
                .toArray();
            for (const row of rows) {
                assertRowEpoch(row, storedProfile.dataset_epoch);
            }
            return rows
                .filter((row) => row.payload.entity_type === 'route_template' &&
                row.payload.value.metadata.tombstone === null)
                .sort((left, right) => left.entity_id.localeCompare(right.entity_id))
                .map((row) => structuredClone(row.payload.value));
        });
    }
    async saveRouteTemplate(mutation) {
        this.assertOpen();
        const routeTemplateId = uuidSchema.parse(mutation.value.route_template_id);
        const operationId = uuidSchema.parse(this.runtime.uuid());
        const now = instantSchema.parse(this.runtime.now());
        const result = await this.database.transaction('rw', [
            this.database.entities,
            this.database.outbox,
            this.database.runtime_state,
            this.database.profiles,
            this.database.device_cursors,
        ], async () => {
            const storedProfile = await this.assertWriteAllowed();
            const runtime = await this.requireRuntimeState();
            const row = await this.database.entities.get(entityTableKey('route_template', routeTemplateId));
            if (row !== undefined) {
                assertRowEpoch(row, storedProfile.dataset_epoch);
            }
            const existing = row?.payload.entity_type === 'route_template' ? row.payload.value : null;
            this.assertExpectedRevision(routeTemplateId, mutation.expected_revision, existing?.metadata.revision ?? null);
            if (existing !== null && existing.metadata.tombstone !== null) {
                throw new UnsupportedStructuralMutationError('soft-deleted route templates require an explicit restore operation');
            }
            const routeTemplate = normalizeRouteTemplateMutation(mutation, existing, now);
            const payload = {
                entity_type: 'route_template',
                value: routeTemplate,
            };
            const operation = makePendingOperation({
                operation_id: operationId,
                device_id: runtime.device_id,
                local_profile_id: storedProfile.local_profile_id,
                dataset_epoch: runtime.dataset_epoch,
                local_sequence: runtime.next_local_sequence,
                operation_origin: 'local_edit',
                base_revision: existing?.metadata.revision ?? null,
                payload,
                created_at: now,
            });
            await this.database.entities.put(entityRowFromPayload(payload, runtime.dataset_epoch));
            this.runtime.fault?.('after_domain_write');
            this.runtime.fault?.('before_outbox_write');
            await this.database.outbox.add(operation);
            await this.database.runtime_state.put({
                ...runtime,
                next_local_sequence: (BigInt(runtime.next_local_sequence) + 1n).toString(),
                mutation_generation: nextSnapshotMutationGeneration(runtime),
            });
            const updatedProfile = localProfileSchema.parse({
                ...storedProfile,
                last_mutation_at: now,
            });
            await this.database.profiles.put({ key: 'profile', value: updatedProfile });
            return { routeTemplate, profile: updatedProfile };
        });
        this.profileValue = result.profile;
        return structuredClone(result.routeTemplate);
    }
    async applyRouteTemplateToDraft(input) {
        this.assertOpen();
        const draftKey = parseDraftKey(input.draft.draft_key);
        if (input.draft.draft_kind !== 'shift_editor') {
            throw new UnsupportedStructuralMutationError('route template application requires a shift-editor draft');
        }
        const draftPayload = shiftEditorDraftSchema.parse(input.draft.payload);
        if (draftKey !== `shift-editor:${draftPayload.shift_id}`) {
            throw new UnsupportedStructuralMutationError('route template application requires the canonical owning-shift draft key');
        }
        const routeTemplateId = uuidSchema.parse(input.template.route_template_id);
        const operationId = uuidSchema.parse(this.runtime.uuid());
        const now = instantSchema.parse(this.runtime.now());
        const result = await this.database.transaction('rw', [
            this.database.entities,
            this.database.outbox,
            this.database.drafts,
            this.database.runtime_state,
            this.database.profiles,
            this.database.device_cursors,
        ], async () => {
            const storedProfile = await this.assertWriteAllowed();
            const runtime = await this.requireRuntimeState();
            const owningShift = await this.requireShift(draftPayload.shift_id, draftPayload.base_aggregate_revision);
            this.assertExpectedRevision(draftPayload.shift_id, draftPayload.base_aggregate_revision, owningShift.aggregate_revision);
            if (owningShift.shift.metadata.tombstone !== null || owningShift.shift.status !== 'open') {
                throw new UnsupportedStructuralMutationError('route template application requires its exact live open shift');
            }
            if (owningShift.shift.service_date !== draftPayload.service_date) {
                throw new UnsupportedStructuralMutationError('route template application draft does not belong to its shift service date');
            }
            const existingDraft = await this.database.drafts
                .where('[local_profile_id+draft_key]')
                .equals([storedProfile.local_profile_id, draftKey])
                .first();
            this.assertDraftVersion(draftKey, input.draft.expected_draft_version, existingDraft === undefined ? null : draftVersion(existingDraft));
            let previousDraftPayload = null;
            if (existingDraft !== undefined) {
                if (existingDraft.draft_kind !== 'shift_editor') {
                    throw new UnsupportedStructuralMutationError('route template application cannot replace another draft kind');
                }
                previousDraftPayload = parseShiftEditorDraft(existingDraft.payload);
                if (previousDraftPayload.shift_id !== draftPayload.shift_id ||
                    previousDraftPayload.trip_segment_id !== draftPayload.trip_segment_id ||
                    previousDraftPayload.service_date !== draftPayload.service_date ||
                    previousDraftPayload.base_aggregate_revision !== draftPayload.base_aggregate_revision) {
                    throw new UnsupportedStructuralMutationError('route template application cannot change the durable draft owner or target');
                }
            }
            const targetSegment = owningShift.segments.find((segment) => segment.trip_segment_id === draftPayload.trip_segment_id);
            if (targetSegment?.metadata.tombstone !== null &&
                targetSegment?.metadata.tombstone !== undefined) {
                throw new UnsupportedStructuralMutationError('route template application cannot target a deleted segment');
            }
            if (previousDraftPayload === null && targetSegment !== undefined) {
                throw new UnsupportedStructuralMutationError('an absent draft can initialize only a new segment');
            }
            const row = await this.database.entities.get(entityTableKey('route_template', routeTemplateId));
            if (row !== undefined)
                assertRowEpoch(row, storedProfile.dataset_epoch);
            const existing = row?.payload.entity_type === 'route_template'
                ? routeTemplateSchema.parse(row.payload.value)
                : null;
            this.assertExpectedRevision(routeTemplateId, input.template.expected_revision, existing?.metadata.revision ?? null);
            if (existing === null || existing.metadata.tombstone !== null) {
                throw new UnsupportedStructuralMutationError('only a live persisted route template can update a draft');
            }
            assertRouteTemplateDraftBinding(draftPayload, existing);
            const template = routeTemplateSchema.parse({
                ...existing,
                use_count: existing.use_count + 1,
                metadata: nextExplicitEntityMetadata(existing, now),
            });
            const persistedPayload = persistedShiftEditorDraftPayload(draftPayload);
            const draft = existingDraft === undefined
                ? {
                    draft_id: uuidSchema.parse(this.runtime.uuid()),
                    draft_key: draftKey,
                    draft_kind: 'shift_editor',
                    ...persistedPayload,
                    local_profile_id: storedProfile.local_profile_id,
                    dataset_epoch: storedProfile.dataset_epoch,
                    autosave_revision: 1,
                    created_at: now,
                    updated_at: now,
                }
                : {
                    ...draftRecordWithoutExpandedPayload(existingDraft),
                    ...persistedPayload,
                    autosave_revision: existingDraft.autosave_revision + 1,
                    updated_at: now,
                };
            const payload = { entity_type: 'route_template', value: template };
            const operation = makePendingOperation({
                operation_id: operationId,
                device_id: runtime.device_id,
                local_profile_id: storedProfile.local_profile_id,
                dataset_epoch: runtime.dataset_epoch,
                local_sequence: runtime.next_local_sequence,
                operation_origin: 'local_edit',
                base_revision: existing.metadata.revision,
                payload,
                created_at: now,
            });
            const profile = localProfileSchema.parse({ ...storedProfile, last_mutation_at: now });
            await this.database.entities.put(entityRowFromPayload(payload, runtime.dataset_epoch));
            await this.database.drafts.put(draft);
            this.runtime.fault?.('after_domain_write');
            this.runtime.fault?.('before_outbox_write');
            await this.database.outbox.add(operation);
            await this.database.runtime_state.put({
                ...runtime,
                next_local_sequence: (BigInt(runtime.next_local_sequence) + 1n).toString(),
                mutation_generation: nextSnapshotMutationGeneration(runtime),
            });
            await this.database.profiles.put({ key: 'profile', value: profile });
            return { draft, route_template: template, profile };
        });
        this.profileValue = result.profile;
        return {
            draft: draftRecordForCurrentReader(result.draft),
            route_template: structuredClone(result.route_template),
        };
    }
    async saveShiftAggregate(mutation, options) {
        return this.persistShiftMutation(mutation, options, null);
    }
    async commitShiftDraft(draftKeyInput, mutation, options) {
        const draftKey = parseDraftKey(draftKeyInput);
        return this.persistShiftMutation(mutation, options, {
            draft_key: draftKey,
            expected_version: parseDraftVersionToken(options.expected_draft_version),
        });
    }
    async commitShiftDraftBundle(draftKeyInput, mutation, options) {
        this.assertOpen();
        const draftKey = parseDraftKey(draftKeyInput);
        const expectedDraftVersion = parseDraftVersionToken(options.expected_draft_version);
        const datasetEpoch = uuidSchema.parse(mutation.dataset_epoch);
        const targetTripSegmentId = uuidSchema.parse(mutation.target_trip_segment_id);
        this.assertEpoch(datasetEpoch);
        if (mutation.shift_aggregate.value.dataset_epoch !== datasetEpoch) {
            throw new DatasetEpochMismatchError(datasetEpoch, mutation.shift_aggregate.value.dataset_epoch);
        }
        if (mutation.user_defined_locations.length > MAX_SHIFT_DRAFT_USER_DEFINED_LOCATIONS) {
            throw new Error(`a shift draft bundle supports at most ${String(MAX_SHIFT_DRAFT_USER_DEFINED_LOCATIONS)} user-defined locations`);
        }
        if (mutation.vehicles.length > 16) {
            throw new Error('a shift draft bundle supports at most 16 vehicles');
        }
        const locationIds = mutation.user_defined_locations.map((locationMutation) => uuidSchema.parse(locationMutation.value.location_id));
        if (new Set(locationIds).size !== locationIds.length) {
            throw new Error('a shift draft bundle cannot mutate a user-defined location twice');
        }
        const vehicleIds = mutation.vehicles.map((vehicleMutation) => uuidSchema.parse(vehicleMutation.value.vehicle_id));
        if (new Set(vehicleIds).size !== vehicleIds.length) {
            throw new Error('a shift draft bundle cannot mutate a vehicle twice');
        }
        const routeVariantMutation = mutation.route_variant;
        const routeUsageChange = routeVariantMutation?.route_usage_change ?? null;
        if (routeUsageChange !== null &&
            routeUsageChange !== 'record_new_use' &&
            routeUsageChange !== 'preserve_existing_use' &&
            routeUsageChange !== 'materialize_legacy_existing_use') {
            throw new UnsupportedStructuralMutationError('unknown route usage change');
        }
        const routeVariantId = routeVariantMutation === null
            ? null
            : uuidSchema.parse(routeVariantMutation.value.route_variant_id);
        const shiftId = uuidSchema.parse(mutation.shift_aggregate.value.shift.shift_id);
        const entityIds = [
            ...locationIds,
            ...vehicleIds,
            ...(routeVariantId === null ? [] : [routeVariantId]),
            shiftId,
        ];
        if (new Set(entityIds).size !== entityIds.length) {
            throw new Error('a shift draft bundle cannot contain duplicate entity IDs');
        }
        const now = instantSchema.parse(this.runtime.now());
        const result = await this.database.transaction('rw', [
            this.database.entities,
            this.database.outbox,
            this.database.runtime_state,
            this.database.profiles,
            this.database.drafts,
            this.database.trip_revisions,
            this.database.device_cursors,
        ], async () => {
            const storedProfile = await this.assertWriteAllowed();
            const runtime = await this.requireRuntimeState();
            const draft = await this.database.drafts
                .where('[local_profile_id+draft_key]')
                .equals([storedProfile.local_profile_id, draftKey])
                .first();
            this.assertDraftVersion(draftKey, expectedDraftVersion, draft === undefined ? null : draftVersion(draft));
            if (draft === undefined) {
                throw new Error('draft CAS accepted an absent draft');
            }
            this.assertEpoch(draft.dataset_epoch);
            const existingLocations = await Promise.all(mutation.user_defined_locations.map(async (locationMutation, index) => {
                const locationId = locationIds[index];
                if (locationId === undefined) {
                    throw new Error('location identity projection is incomplete');
                }
                const row = await this.database.entities.get(entityTableKey('user_defined_location', locationId));
                if (row !== undefined) {
                    assertRowEpoch(row, storedProfile.dataset_epoch);
                }
                const existing = row?.payload.entity_type === 'user_defined_location' ? row.payload.value : null;
                this.assertExpectedRevision(locationId, locationMutation.expected_revision, existing?.metadata.revision ?? null);
                if (existing !== null && existing.metadata.tombstone !== null) {
                    throw new UnsupportedStructuralMutationError('soft-deleted user-defined locations require an explicit restore operation');
                }
                return existing;
            }));
            const existingVehicles = await Promise.all(mutation.vehicles.map(async (vehicleMutation, index) => {
                const vehicleId = vehicleIds[index];
                if (vehicleId === undefined) {
                    throw new Error('vehicle identity projection is incomplete');
                }
                const row = await this.database.entities.get(entityTableKey('vehicle', vehicleId));
                if (row !== undefined) {
                    assertRowEpoch(row, storedProfile.dataset_epoch);
                }
                const existing = row?.payload.entity_type === 'vehicle' ? row.payload.value : null;
                this.assertExpectedRevision(vehicleId, vehicleMutation.expected_revision, existing?.metadata.revision ?? null);
                if (existing !== null && existing.metadata.tombstone !== null) {
                    throw new UnsupportedStructuralMutationError('soft-deleted vehicles require an explicit restore operation');
                }
                return existing;
            }));
            const existingRouteRow = routeVariantId === null
                ? undefined
                : await this.database.entities.get(entityTableKey('route_variant', routeVariantId));
            if (existingRouteRow !== undefined) {
                assertRowEpoch(existingRouteRow, storedProfile.dataset_epoch);
            }
            const existingRoute = existingRouteRow?.payload.entity_type === 'route_variant'
                ? existingRouteRow.payload.value
                : null;
            if (routeVariantMutation !== null && routeVariantId !== null) {
                this.assertExpectedRevision(routeVariantId, routeVariantMutation.expected_revision, existingRoute?.metadata.revision ?? null);
            }
            if (existingRoute !== null && existingRoute.metadata.tombstone !== null) {
                throw new UnsupportedStructuralMutationError('soft-deleted route variants require an explicit restore operation');
            }
            const existingShiftRow = await this.database.entities.get(entityTableKey('shift_aggregate', shiftId));
            if (existingShiftRow !== undefined) {
                assertRowEpoch(existingShiftRow, storedProfile.dataset_epoch);
            }
            const existingShift = existingShiftRow?.payload.entity_type === 'shift_aggregate'
                ? existingShiftRow.payload.value
                : null;
            this.assertExpectedRevision(shiftId, mutation.shift_aggregate.expected_revision, existingShift?.aggregate_revision ?? null);
            if (existingShift !== null && existingShift.shift.metadata.tombstone !== null) {
                throw new UnsupportedStructuralMutationError('soft-deleted shifts must use the explicit restore operation');
            }
            // Every CAS check above completes before the first canonical entity is written.
            const expandedWire = expandedRouteDraftWireActive(runtime);
            const locations = mutation.user_defined_locations.map((locationMutation, index) => normalizeUserDefinedLocationMutation(locationMutation, existingLocations[index] ?? null, now));
            const vehicles = mutation.vehicles.map((vehicleMutation, index) => normalizeVehicleMutation(vehicleMutation, existingVehicles[index] ?? null, now));
            const routeVariant = routeVariantMutation === null
                ? null
                : normalizeRouteVariantMutation(routeVariantMutation, existingRoute, now, expandedWire);
            // A legacy snapshot can name an identity that was never materialized,
            // even when another deployed UUID already owns the same fingerprint.
            // The cross-link validator below proves that materialization is bound
            // to that exact prior snapshot; only genuinely new uses need the
            // global uniqueness guard.
            if (routeVariant !== null && routeUsageChange === 'record_new_use') {
                await this.assertUniqueLiveRouteFingerprint(routeVariant, existingRoute);
            }
            const shiftAggregate = normalizeShiftMutation(mutation.shift_aggregate.value, existingShift, now, expandedWire);
            validateShiftDraftBundleCrossLinks(locations, existingLocations, vehicles, routeVariant, routeUsageChange, existingRoute, shiftAggregate, existingShift, targetTripSegmentId);
            const changedTarget = shiftAggregate.segments.find((segment) => segment.trip_segment_id === targetTripSegmentId);
            if (changedTarget === undefined) {
                throw new Error('validated target trip segment is absent from the normalized aggregate');
            }
            const affectedRouteIds = changedRouteIds(existingShift, shiftAggregate);
            const aggregatesAfterCommit = await collectShiftAggregatesReplacing(this.database, storedProfile.dataset_epoch, shiftAggregate);
            assertShiftAggregatesUseAuthorizedWire(aggregatesAfterCommit, expandedWire);
            const routeChanges = [];
            for (const routeId of affectedRouteIds) {
                const candidate = routeVariant?.route_variant_id === routeId
                    ? routeVariant
                    : await findLiveRouteVariant(this.database, storedProfile.dataset_epoch, routeId);
                const previous = existingRoute?.route_variant_id === routeId
                    ? existingRoute
                    : await findLiveRouteVariant(this.database, storedProfile.dataset_epoch, routeId);
                // Restored legacy backups may intentionally carry aggregate-only
                // snapshots. Removing or replacing one such use must not require a
                // learned entity that never existed. A retained tombstone still
                // fails closed in findLiveRouteVariant above.
                if (candidate === null)
                    continue;
                const recomputed = recomputeRouteLearning(candidate, aggregatesAfterCommit, now, previous, expandedWire);
                if (previous !== null && routeVariantLearningMatches(previous, recomputed))
                    continue;
                routeChanges.push({ previous, value: recomputed });
            }
            const savedRouteVariant = routeVariantId === null
                ? null
                : (routeChanges.find(({ value }) => value.route_variant_id === routeVariantId)?.value ??
                    (existingRoute?.route_variant_id === routeVariantId ? existingRoute : null));
            if (routeVariantId !== null && savedRouteVariant === null) {
                throw new Error('validated route variant is absent from the recomputed bundle');
            }
            const payloads = [
                ...locations.map((location) => ({
                    entity_type: 'user_defined_location',
                    value: location,
                })),
                ...vehicles.map((vehicle) => ({
                    entity_type: 'vehicle',
                    value: vehicle,
                })),
                ...routeChanges.map(({ value }) => ({
                    entity_type: 'route_variant',
                    value,
                })),
                { entity_type: 'shift_aggregate', value: shiftAggregate },
            ];
            const previousRevisions = [
                ...existingLocations.map((location) => location?.metadata.revision ?? null),
                ...existingVehicles.map((vehicle) => vehicle?.metadata.revision ?? null),
                ...routeChanges.map(({ previous }) => previous?.metadata.revision ?? null),
                existingShift?.aggregate_revision ?? null,
            ];
            const operationIds = payloads.map(() => uuidSchema.parse(this.runtime.uuid()));
            const operations = payloads.map((payload, index) => {
                const operationId = operationIds[index];
                const previousRevision = previousRevisions[index];
                if (operationId === undefined || previousRevision === undefined) {
                    throw new Error('bundle operation projection is incomplete');
                }
                return makePendingOperation({
                    operation_id: operationId,
                    device_id: runtime.device_id,
                    local_profile_id: storedProfile.local_profile_id,
                    dataset_epoch: runtime.dataset_epoch,
                    local_sequence: (BigInt(runtime.next_local_sequence) + BigInt(index)).toString(),
                    operation_origin: options.operation_origin ?? 'local_edit',
                    base_revision: previousRevision,
                    payload,
                    created_at: now,
                });
            });
            await this.database.entities.bulkPut(payloads.map((payload) => entityRowFromPayload(payload, datasetEpoch)));
            await this.writeTripRevisionDelta(existingShift, shiftAggregate, 'upsert', runtime.device_id, now);
            this.runtime.fault?.('after_domain_write');
            this.runtime.fault?.('before_outbox_write');
            await this.database.outbox.bulkAdd(operations);
            await this.database.runtime_state.put({
                ...runtime,
                next_local_sequence: (BigInt(runtime.next_local_sequence) + BigInt(operations.length)).toString(),
                mutation_generation: nextSnapshotMutationGeneration(runtime),
            });
            const updatedProfile = localProfileSchema.parse({
                ...storedProfile,
                last_mutation_at: now,
            });
            await this.database.profiles.put({ key: 'profile', value: updatedProfile });
            await this.database.drafts.delete(draft.draft_id);
            this.runtime.fault?.('after_draft_delete');
            return {
                locations,
                vehicles,
                routeVariant: savedRouteVariant,
                shiftAggregate,
                profile: updatedProfile,
            };
        });
        this.profileValue = result.profile;
        return {
            user_defined_locations: structuredClone(result.locations),
            vehicles: structuredClone(result.vehicles),
            route_variant: structuredClone(result.routeVariant),
            shift_aggregate: structuredClone(result.shiftAggregate),
        };
    }
    async undoLatestSegmentSave(input) {
        this.assertOpen();
        const shiftId = uuidSchema.parse(input.shift_id);
        const tripSegmentId = uuidSchema.parse(input.trip_segment_id);
        const expectedAggregateRevision = revisionSchema.parse(input.expected_aggregate_revision);
        const expectedTargetSegmentRevision = revisionSchema.parse(input.expected_target_segment_revision);
        const operationId = uuidSchema.parse(this.runtime.uuid());
        const now = instantSchema.parse(this.runtime.now());
        const result = await this.database.transaction('rw', [
            this.database.entities,
            this.database.outbox,
            this.database.runtime_state,
            this.database.profiles,
            this.database.trip_revisions,
            this.database.device_cursors,
        ], async () => {
            const storedProfile = await this.assertWriteAllowed();
            const runtime = await this.requireRuntimeState();
            const current = await this.requireShift(shiftId, expectedAggregateRevision);
            this.assertExpectedRevision(shiftId, expectedAggregateRevision, current.aggregate_revision);
            if (current.shift.metadata.tombstone !== null) {
                throw new UnsupportedStructuralMutationError('a segment save cannot be undone inside a soft-deleted shift');
            }
            const currentTarget = current.segments.find((segment) => segment.trip_segment_id === tripSegmentId);
            this.assertExpectedRevision(tripSegmentId, expectedTargetSegmentRevision, currentTarget?.metadata.revision ?? null);
            if (currentTarget === undefined) {
                throw new Error('segment CAS accepted an absent target');
            }
            const revisions = (await this.database.trip_revisions
                .where('trip_segment_id')
                .equals(tripSegmentId)
                .toArray()).map((revision) => tripRevisionSchema.parse(revision));
            const wrongOwner = revisions.find((revision) => revision.shift_id !== shiftId);
            if (wrongOwner !== undefined) {
                throw new UnsupportedStructuralMutationError('trip revision ownership does not match the undo target shift');
            }
            const staleEpoch = revisions.find((revision) => revision.dataset_epoch !== current.dataset_epoch);
            if (staleEpoch !== undefined) {
                throw new DatasetEpochMismatchError(current.dataset_epoch, staleEpoch.dataset_epoch);
            }
            const orderedRevisions = revisions.sort((left, right) => left.resulting_revision - right.resulting_revision ||
                compareInstants(left.recorded_at, right.recorded_at) ||
                left.trip_revision_id.localeCompare(right.trip_revision_id));
            const revisionsAtTarget = orderedRevisions.filter((revision) => revision.resulting_revision === currentTarget.metadata.revision);
            const latestRevision = orderedRevisions.at(-1);
            if (revisionsAtTarget.length !== 1 ||
                latestRevision === undefined ||
                latestRevision !== revisionsAtTarget[0] ||
                latestRevision.after === null ||
                !sameJson(latestRevision.after, currentTarget)) {
                throw new UnsupportedStructuralMutationError('latest trip revision does not exactly match the current undo target');
            }
            const previousSemantic = latestRevision.before ?? currentTarget;
            const { metadata: ignoredMetadata, ...previousWithoutMetadata } = previousSemantic;
            void ignoredMetadata;
            const needsTombstone = latestRevision.before === null || latestRevision.before.metadata.tombstone !== null;
            const tombstone = needsTombstone
                ? tombstoneLifecycleSchema.parse({
                    deleted_at: now,
                    restore_until: new Date(Date.parse(now) + TRASH_WINDOW_MILLISECONDS).toISOString(),
                    deletion_operation_id: operationId,
                    deletion_cursor: null,
                    dataset_epoch: current.dataset_epoch,
                })
                : null;
            const undoneTarget = {
                ...previousWithoutMetadata,
                metadata: {
                    created_at: currentTarget.metadata.created_at,
                    updated_at: now,
                    revision: currentTarget.metadata.revision + 1,
                    tombstone,
                },
            };
            const undone = shiftAggregateSchema.parse({
                ...current,
                aggregate_revision: current.aggregate_revision + 1,
                shift: {
                    ...current.shift,
                    metadata: incrementLiveMetadata(current.shift.metadata, now),
                },
                segments: current.segments.map((segment) => segment.trip_segment_id === tripSegmentId ? undoneTarget : segment),
            });
            const undoTripRevisionOperationKind = undoneTarget.metadata.tombstone !== null
                ? 'soft_delete'
                : currentTarget.metadata.tombstone !== null
                    ? 'restore'
                    : 'upsert';
            const affectedRouteIds = [
                currentTarget.route_snapshot?.route_variant_id,
                undoneTarget.route_snapshot?.route_variant_id,
            ]
                .filter((routeId) => routeId !== undefined)
                .filter((routeId, index, routeIds) => routeIds.indexOf(routeId) === index)
                .sort();
            const shiftRows = await this.database.entities
                .where('entity_type')
                .equals('shift_aggregate')
                .toArray();
            const aggregatesAfterUndo = [];
            for (const row of shiftRows) {
                assertRowEpoch(row, storedProfile.dataset_epoch);
                if (row.payload.entity_type !== 'shift_aggregate') {
                    throw new Error('shift aggregate index returned another entity type');
                }
                aggregatesAfterUndo.push(row.entity_id === shiftId ? undone : shiftAggregateSchema.parse(row.payload.value));
            }
            const routeChanges = await recomputeChangedLiveRoutes(this.database, storedProfile.dataset_epoch, affectedRouteIds, aggregatesAfterUndo, now, expandedRouteDraftWireActive(runtime));
            const payloads = [
                ...routeChanges.map(({ value }) => ({
                    entity_type: 'route_variant',
                    value,
                })),
                { entity_type: 'shift_aggregate', value: undone },
            ];
            const previousRevisions = [
                ...routeChanges.map(({ previous }) => previous.metadata.revision),
                current.aggregate_revision,
            ];
            const operationIds = [
                ...routeChanges.map(() => uuidSchema.parse(this.runtime.uuid())),
                operationId,
            ];
            const operations = payloads.map((payload, index) => {
                const undoOperationId = operationIds[index];
                const previousRevision = previousRevisions[index];
                if (undoOperationId === undefined || previousRevision === undefined) {
                    throw new Error('undo operation projection is incomplete');
                }
                return makePendingOperation({
                    operation_id: undoOperationId,
                    device_id: runtime.device_id,
                    local_profile_id: storedProfile.local_profile_id,
                    dataset_epoch: runtime.dataset_epoch,
                    local_sequence: (BigInt(runtime.next_local_sequence) + BigInt(index)).toString(),
                    operation_origin: 'local_edit',
                    base_revision: previousRevision,
                    payload,
                    created_at: now,
                });
            });
            await this.database.entities.bulkPut(payloads.map((payload) => entityRowFromPayload(payload, runtime.dataset_epoch)));
            await this.writeTripRevisionDelta(current, undone, undoTripRevisionOperationKind, runtime.device_id, now);
            this.runtime.fault?.('after_domain_write');
            this.runtime.fault?.('before_outbox_write');
            await this.database.outbox.bulkAdd(operations);
            await this.database.runtime_state.put({
                ...runtime,
                next_local_sequence: (BigInt(runtime.next_local_sequence) + BigInt(operations.length)).toString(),
                mutation_generation: nextSnapshotMutationGeneration(runtime),
            });
            const profile = localProfileSchema.parse({
                ...storedProfile,
                last_mutation_at: now,
            });
            await this.database.profiles.put({ key: 'profile', value: profile });
            return { aggregate: undone, profile };
        });
        this.profileValue = result.profile;
        return structuredClone(result.aggregate);
    }
    async autosaveDraft(input) {
        this.assertOpen();
        const draftKey = parseDraftKey(input.draft_key);
        const payload = persistedShiftEditorDraftPayloadFromUnknown(input.payload);
        const now = instantSchema.parse(this.runtime.now());
        const result = await this.database.transaction('rw', [
            this.database.drafts,
            this.database.profiles,
            this.database.runtime_state,
            this.database.outbox,
            this.database.device_cursors,
        ], async () => {
            const storedProfile = await this.assertWriteAllowed();
            const runtime = await this.requireRuntimeState();
            const existing = await this.database.drafts
                .where('[local_profile_id+draft_key]')
                .equals([storedProfile.local_profile_id, draftKey])
                .first();
            this.assertDraftVersion(draftKey, input.expected_draft_version, existing === undefined ? null : draftVersion(existing));
            const draft = {
                draft_id: existing?.draft_id ?? uuidSchema.parse(this.runtime.uuid()),
                draft_key: draftKey,
                draft_kind: input.draft_kind,
                ...payload,
                local_profile_id: storedProfile.local_profile_id,
                dataset_epoch: storedProfile.dataset_epoch,
                autosave_revision: (existing?.autosave_revision ?? 0) + 1,
                created_at: existing?.created_at ?? now,
                updated_at: now,
            };
            const updatedProfile = localProfileSchema.parse({
                ...storedProfile,
                last_mutation_at: now,
            });
            await this.database.drafts.put(draft);
            await this.database.profiles.put({ key: 'profile', value: updatedProfile });
            await this.database.runtime_state.put({
                ...runtime,
                mutation_generation: nextSnapshotMutationGeneration(runtime),
            });
            return { draft, profile: updatedProfile };
        });
        this.profileValue = result.profile;
        return draftRecordForCurrentReader(result.draft);
    }
    async getDraft(draftKeyInput) {
        this.assertOpen();
        const draftKey = parseDraftKey(draftKeyInput);
        return this.readConsistently([this.database.drafts], async (storedProfile) => {
            const draft = await this.database.drafts
                .where('[local_profile_id+draft_key]')
                .equals([storedProfile.local_profile_id, draftKey])
                .first();
            if (draft === undefined) {
                return null;
            }
            if (draft.dataset_epoch !== storedProfile.dataset_epoch) {
                throw new DatasetEpochMismatchError(storedProfile.dataset_epoch, draft.dataset_epoch);
            }
            return draftRecordForCurrentReader(draft);
        });
    }
    async discardDraft(draftKeyInput, expectedDraftVersionInput) {
        this.assertOpen();
        const draftKey = parseDraftKey(draftKeyInput);
        const expectedDraftVersion = parseDraftVersionToken(expectedDraftVersionInput);
        const now = instantSchema.parse(this.runtime.now());
        const updatedProfile = await this.database.transaction('rw', [
            this.database.drafts,
            this.database.profiles,
            this.database.runtime_state,
            this.database.outbox,
            this.database.device_cursors,
        ], async () => {
            const storedProfile = await this.assertWriteAllowed();
            const runtime = await this.requireRuntimeState();
            const draft = await this.database.drafts
                .where('[local_profile_id+draft_key]')
                .equals([storedProfile.local_profile_id, draftKey])
                .first();
            this.assertDraftVersion(draftKey, expectedDraftVersion, draft === undefined ? null : draftVersion(draft));
            if (draft === undefined) {
                throw new Error('draft CAS accepted an absent draft');
            }
            this.assertEpoch(draft.dataset_epoch);
            const profile = localProfileSchema.parse({
                ...storedProfile,
                last_mutation_at: now,
            });
            await this.database.drafts.delete(draft.draft_id);
            await this.database.profiles.put({ key: 'profile', value: profile });
            await this.database.runtime_state.put({
                ...runtime,
                mutation_generation: nextSnapshotMutationGeneration(runtime),
            });
            return profile;
        });
        this.profileValue = updatedProfile;
    }
    async pendingOutbox() {
        this.assertOpen();
        return this.readConsistently([this.database.outbox], async (storedProfile) => {
            const operations = (await this.database.outbox.toArray()).map((operation) => pendingSyncOperationSchema.parse(operation));
            const stale = operations.find((operation) => operation.dataset_epoch !== storedProfile.dataset_epoch);
            if (stale) {
                throw new DatasetEpochMismatchError(storedProfile.dataset_epoch, stale.dataset_epoch);
            }
            return operations
                .sort((left, right) => compareDecimalCursors(left.local_sequence, right.local_sequence) ||
                left.operation_id.localeCompare(right.operation_id))
                .map((operation) => structuredClone(operation));
        });
    }
    async softDeleteShift(shiftIdInput, expectedRevision) {
        this.assertOpen();
        const shiftId = uuidSchema.parse(shiftIdInput);
        const operationId = uuidSchema.parse(this.runtime.uuid());
        const now = instantSchema.parse(this.runtime.now());
        const restoreUntil = new Date(Date.parse(now) + TRASH_WINDOW_MILLISECONDS).toISOString();
        const tombstone = tombstoneLifecycleSchema.parse({
            deleted_at: now,
            restore_until: restoreUntil,
            deletion_operation_id: operationId,
            deletion_cursor: null,
            dataset_epoch: this.profileValue.dataset_epoch,
        });
        const result = await this.database.transaction('rw', [
            this.database.entities,
            this.database.outbox,
            this.database.runtime_state,
            this.database.profiles,
            this.database.trip_revisions,
            this.database.device_cursors,
        ], async () => {
            const storedProfile = await this.assertWriteAllowed();
            const runtime = await this.requireRuntimeState();
            const existing = await this.requireShift(shiftId, expectedRevision);
            this.assertExpectedRevision(shiftId, expectedRevision, existing.aggregate_revision);
            if (existing.shift.metadata.tombstone !== null) {
                throw new UnsupportedStructuralMutationError('shift is already soft-deleted');
            }
            const deleted = shiftAggregateSchema.parse({
                ...existing,
                aggregate_revision: existing.aggregate_revision + 1,
                shift: {
                    ...existing.shift,
                    metadata: incrementDeletedMetadata(existing.shift.metadata, now, tombstone),
                },
                segments: existing.segments.map((segment) => ({
                    ...segment,
                    metadata: incrementDeletedMetadata(segment.metadata, now, tombstone),
                })),
            });
            const profile = await this.writeShiftLifecycleAndRouteLearning(deleted, existing, 'soft_delete', operationId, now, storedProfile, runtime);
            return { aggregate: deleted, profile };
        });
        this.profileValue = result.profile;
        return structuredClone(result.aggregate);
    }
    async restoreShift(shiftIdInput, expectedRevision) {
        this.assertOpen();
        const shiftId = uuidSchema.parse(shiftIdInput);
        const operationId = uuidSchema.parse(this.runtime.uuid());
        const now = instantSchema.parse(this.runtime.now());
        const result = await this.database.transaction('rw', [
            this.database.entities,
            this.database.outbox,
            this.database.runtime_state,
            this.database.profiles,
            this.database.trip_revisions,
            this.database.device_cursors,
        ], async () => {
            const storedProfile = await this.assertWriteAllowed();
            const runtime = await this.requireRuntimeState();
            const existing = await this.requireShift(shiftId, expectedRevision);
            this.assertExpectedRevision(shiftId, expectedRevision, existing.aggregate_revision);
            const tombstone = existing.shift.metadata.tombstone;
            if (tombstone === null) {
                throw new UnsupportedStructuralMutationError('shift is not soft-deleted');
            }
            if (compareInstants(now, tombstone.restore_until) >= 0) {
                throw new TrashRestoreWindowExpiredError(shiftId);
            }
            this.assertEpoch(tombstone.dataset_epoch);
            const revisions = (await this.database.trip_revisions.where('shift_id').equals(shiftId).toArray()).map((revision) => tripRevisionSchema.parse(revision));
            const deletionRevisions = new Map();
            for (const segment of existing.segments) {
                const revision = revisions.find((candidate) => candidate.trip_segment_id === segment.trip_segment_id &&
                    candidate.resulting_revision === segment.metadata.revision &&
                    candidate.change_kind === 'soft_delete' &&
                    candidate.after !== null &&
                    sameJson(candidate.after, segment));
                if (revision === undefined || revision.before === null) {
                    throw new UnsupportedStructuralMutationError('shift deletion history does not exactly match every restore target');
                }
                deletionRevisions.set(segment.trip_segment_id, revision);
            }
            const restored = shiftAggregateSchema.parse({
                ...existing,
                aggregate_revision: existing.aggregate_revision + 1,
                shift: {
                    ...existing.shift,
                    metadata: incrementRestoredMetadata(existing.shift.metadata, now),
                },
                segments: existing.segments.map((segment) => {
                    const deletionRevision = deletionRevisions.get(segment.trip_segment_id);
                    if (deletionRevision?.before?.metadata.tombstone === null) {
                        return {
                            ...segment,
                            metadata: incrementRestoredMetadata(segment.metadata, now),
                        };
                    }
                    const previousTombstone = deletionRevision?.before?.metadata.tombstone;
                    if (previousTombstone === null || previousTombstone === undefined) {
                        throw new UnsupportedStructuralMutationError('shift deletion history lost the previous segment tombstone');
                    }
                    const restoredTombstone = tombstoneLifecycleSchema.parse({
                        ...previousTombstone,
                        deleted_at: now,
                        restore_until: new Date(Date.parse(now) + TRASH_WINDOW_MILLISECONDS).toISOString(),
                    });
                    return {
                        ...segment,
                        metadata: {
                            ...segment.metadata,
                            updated_at: now,
                            revision: segment.metadata.revision + 1,
                            tombstone: restoredTombstone,
                        },
                    };
                }),
            });
            const profile = await this.writeShiftLifecycleAndRouteLearning(restored, existing, 'restore', operationId, now, storedProfile, runtime);
            return { aggregate: restored, profile };
        });
        this.profileValue = result.profile;
        return structuredClone(result.aggregate);
    }
    async listTrash(nowInput = this.runtime.now()) {
        this.assertOpen();
        const now = instantSchema.parse(nowInput);
        return this.readConsistently([this.database.entities], async (storedProfile) => {
            const rows = await this.database.entities.where('tombstone_deleted_at').above('').toArray();
            for (const row of rows) {
                assertRowEpoch(row, storedProfile.dataset_epoch);
            }
            return rows
                .map((row) => {
                const tombstone = rootTombstone(row.payload);
                if (tombstone === null) {
                    return null;
                }
                return {
                    entity_type: row.entity_type,
                    entity_id: row.entity_id,
                    deleted_at: tombstone.deleted_at,
                    restore_until: tombstone.restore_until,
                    recoverable: compareInstants(now, tombstone.restore_until) < 0,
                };
            })
                .filter((entry) => entry !== null)
                .sort((left, right) => left.deleted_at.localeCompare(right.deleted_at));
        });
    }
    async purgeAcknowledgedTombstones(nowInput = this.runtime.now()) {
        this.assertOpen();
        const now = instantSchema.parse(nowInput);
        return this.database.transaction('rw', [
            this.database.entities,
            this.database.device_cursors,
            this.database.profiles,
            this.database.runtime_state,
            this.database.outbox,
            this.database.trip_revisions,
        ], async () => {
            const storedProfile = await this.requireActiveStoredProfile();
            const runtime = await this.requireRuntimeState();
            const rows = await this.database.entities.where('tombstone_deleted_at').above('').toArray();
            const liveReferencedRouteIds = new Set();
            const shiftRows = await this.database.entities
                .where('entity_type')
                .equals('shift_aggregate')
                .toArray();
            for (const shiftRow of shiftRows) {
                assertRowEpoch(shiftRow, storedProfile.dataset_epoch);
                if (shiftRow.payload.entity_type !== 'shift_aggregate') {
                    throw new Error('shift aggregate index returned another entity type');
                }
                const aggregate = shiftAggregateSchema.parse(shiftRow.payload.value);
                if (aggregate.shift.metadata.tombstone !== null)
                    continue;
                for (const segment of aggregate.segments) {
                    if (segment.metadata.tombstone === null &&
                        (segment.status === 'complete' || segment.status === 'partial') &&
                        segment.route_snapshot !== null) {
                        liveReferencedRouteIds.add(segment.route_snapshot.route_variant_id);
                    }
                }
            }
            const cursors = await this.database.device_cursors.toArray();
            const purged = [];
            const retained = [];
            for (const row of rows) {
                const tombstone = rootTombstone(row.payload);
                const retainedReferencedRoute = row.entity_type === 'route_variant' && liveReferencedRouteIds.has(row.entity_id);
                if (tombstone !== null &&
                    !retainedReferencedRoute &&
                    evaluateTombstonePurge(tombstone, cursors, now).purge_allowed) {
                    if (row.entity_type === 'shift_aggregate') {
                        await this.database.trip_revisions.where('shift_id').equals(row.entity_id).delete();
                    }
                    await this.database.entities.delete(row.compound_key);
                    purged.push(row.entity_id);
                }
                else {
                    retained.push(row.entity_id);
                }
            }
            if (purged.length > 0) {
                await this.database.runtime_state.put({
                    ...runtime,
                    mutation_generation: nextSnapshotMutationGeneration(runtime),
                });
            }
            return {
                purged_entity_ids: purged.sort(),
                retained_entity_ids: retained.sort(),
            };
        });
    }
    async recordDeviceCursor(cursorInput) {
        this.assertOpen();
        await this.database.transaction('rw', [this.database.device_cursors, this.database.profiles, this.database.runtime_state], async () => {
            const storedProfile = await this.requireActiveStoredProfile();
            const runtime = await this.requireRuntimeState();
            const changed = await putValidatedDeviceCursor(this.database.device_cursors, deviceCursorSchema.parse(cursorInput), storedProfile);
            if (changed) {
                await this.database.runtime_state.put({
                    ...runtime,
                    mutation_generation: nextSnapshotMutationGeneration(runtime),
                });
            }
        });
    }
    async recordSyncAndExportEvidence(input) {
        this.assertOpen();
        const lastSuccessfulSyncAt = instantSchema.parse(input.last_successful_sync_at);
        const verifiedExport = verifiedExportEvidenceSchema.parse(input.verified_export);
        const coveredMutationGeneration = decimalCursorSchema.parse(input.covered_mutation_generation);
        const updatedProfile = await this.database.transaction('rw', [this.database.outbox, this.database.profiles, this.database.runtime_state], async () => {
            const storedProfile = await this.requireActiveStoredProfile();
            const runtime = await this.requireRuntimeState();
            if (runtime.mutation_generation !== coveredMutationGeneration) {
                throw new Error('local data changed after the export snapshot was created');
            }
            if ((await this.database.outbox.count()) !== 0) {
                throw new Error('cannot mark account-switch protection evidence while outbox is not empty');
            }
            if (verifiedExport.local_profile_id !== storedProfile.local_profile_id) {
                throw new Error('verified local export belongs to another local profile');
            }
            if (verifiedExport.dataset_epoch !== storedProfile.dataset_epoch) {
                throw new DatasetEpochMismatchError(storedProfile.dataset_epoch, verifiedExport.dataset_epoch);
            }
            const lastMutationAt = storedProfile.last_mutation_at;
            if (lastMutationAt !== null &&
                (compareInstants(lastSuccessfulSyncAt, lastMutationAt) < 0 ||
                    compareInstants(verifiedExport.verified_at, lastMutationAt) < 0)) {
                throw new Error('sync and verified export must cover the latest local mutation');
            }
            const profile = localProfileSchema.parse({
                ...storedProfile,
                last_successful_sync_at: lastSuccessfulSyncAt,
                last_verified_export: verifiedExport,
            });
            if (sameJson(profile, storedProfile) &&
                runtime.protected_mutation_generation === runtime.mutation_generation) {
                return storedProfile;
            }
            const nextGeneration = nextSnapshotMutationGeneration(runtime);
            await this.database.profiles.put({ key: 'profile', value: profile });
            await this.database.runtime_state.put({
                ...runtime,
                mutation_generation: nextGeneration,
                protected_mutation_generation: nextGeneration,
            });
            return profile;
        });
        this.profileValue = updatedProfile;
    }
    async recordVerifiedLocalExport(input) {
        this.assertOpen();
        const verifiedExport = verifiedExportEvidenceSchema.parse(input.verified_export);
        const coveredMutationGeneration = decimalCursorSchema.parse(input.covered_mutation_generation);
        const updatedProfile = await this.database.transaction('rw', [this.database.profiles, this.database.runtime_state], async () => {
            const storedProfile = await this.requireActiveStoredProfile();
            const runtime = await this.requireRuntimeState();
            if (runtime.dataset_epoch !== storedProfile.dataset_epoch) {
                throw new DatasetEpochMismatchError(storedProfile.dataset_epoch, runtime.dataset_epoch);
            }
            if (verifiedExport.local_profile_id !== storedProfile.local_profile_id ||
                verifiedExport.dataset_epoch !== storedProfile.dataset_epoch) {
                throw new DatasetEpochMismatchError(storedProfile.dataset_epoch, verifiedExport.dataset_epoch);
            }
            if (runtime.mutation_generation !== coveredMutationGeneration) {
                throw new Error('local data changed after the export snapshot was created');
            }
            if (storedProfile.last_mutation_at !== null &&
                compareInstants(verifiedExport.verified_at, storedProfile.last_mutation_at) < 0) {
                throw new Error('verified local export must cover the latest local mutation');
            }
            const profile = localProfileSchema.parse({
                ...storedProfile,
                last_verified_export: verifiedExport,
            });
            if (sameJson(profile, storedProfile) &&
                runtime.protected_mutation_generation === runtime.mutation_generation) {
                return storedProfile;
            }
            const nextGeneration = nextSnapshotMutationGeneration(runtime);
            await this.database.profiles.put({ key: 'profile', value: profile });
            await this.database.runtime_state.put({
                ...runtime,
                mutation_generation: nextGeneration,
                protected_mutation_generation: nextGeneration,
            });
            return profile;
        });
        this.profileValue = updatedProfile;
    }
    async acknowledgeOperation(operationIdInput, serverCursorInput, currentDeviceCursorInput) {
        this.assertOpen();
        const operationId = uuidSchema.parse(operationIdInput);
        const serverCursor = decimalCursorSchema.parse(serverCursorInput);
        const deviceCursor = deviceCursorSchema.parse(currentDeviceCursorInput);
        this.assertEpoch(deviceCursor.dataset_epoch);
        if (compareDecimalCursors(deviceCursor.last_applied_cursor, serverCursor) < 0) {
            throw new FullResyncRequiredError('device cursor does not acknowledge the server operation');
        }
        await this.database.transaction('rw', [
            this.database.entities,
            this.database.outbox,
            this.database.device_cursors,
            this.database.profiles,
            this.database.runtime_state,
            this.database.trip_revisions,
        ], async () => {
            const storedProfile = await this.requireActiveStoredProfile();
            const runtime = await this.requireRuntimeState();
            const operationInput = await this.database.outbox.get(operationId);
            if (operationInput === undefined) {
                return;
            }
            const operation = pendingSyncOperationSchema.parse(operationInput);
            this.assertEpoch(operation.dataset_epoch);
            if (operation.operation_kind === 'soft_delete') {
                const row = await this.database.entities.get([
                    operation.payload.entity_type,
                    operation.entity_id,
                ]);
                if (row !== undefined && operationStillOwnsTombstone(operation, row)) {
                    const payload = assignDeletionCursor(row.payload, serverCursor);
                    await this.database.entities.put(entityRowFromPayload(payload, row.dataset_epoch));
                    if (payload.entity_type === 'shift_aggregate') {
                        await assignShiftDeletionCursorToLatestTripRevisions(this.database, payload.value, operation, serverCursor);
                    }
                }
            }
            await this.database.outbox.delete(operationId);
            await putValidatedDeviceCursor(this.database.device_cursors, deviceCursor, storedProfile);
            await this.database.runtime_state.put({
                ...runtime,
                mutation_generation: nextSnapshotMutationGeneration(runtime),
            });
        });
    }
    async assertOperationReplayable(operationInput) {
        this.assertOpen();
        const operation = syncOperationSchema.parse(operationInput);
        await this.readConsistently([], async (storedProfile) => {
            if (operation.dataset_epoch !== storedProfile.dataset_epoch) {
                throw new DatasetEpochMismatchError(storedProfile.dataset_epoch, operation.dataset_epoch);
            }
        });
    }
    async createReplacementCheckpoint(options = {}) {
        this.assertOpen();
        const checkpointId = uuidSchema.parse(this.runtime.uuid());
        const createdAt = instantSchema.parse(this.runtime.now());
        const expectedMutationGeneration = options.expected_mutation_generation === undefined
            ? undefined
            : decimalCursorSchema.parse(options.expected_mutation_generation);
        const snapshot = await this.database.transaction('r', [
            this.database.entities,
            this.database.outbox,
            this.database.drafts,
            this.database.profiles,
            this.database.runtime_state,
            this.database.device_cursors,
            this.database.trip_revisions,
        ], async () => {
            const current = await this.readReplacementCheckpointSnapshot();
            if (expectedMutationGeneration !== undefined &&
                current.runtime_state.mutation_generation !== expectedMutationGeneration) {
                throw new Error('local data changed after the restore preview was created');
            }
            return current;
        });
        if (snapshot.profile.account_identity !== null) {
            throw new UnsupportedStructuralMutationError('account-bound replacement checkpoints must be created and authorized by the server');
        }
        const payloadSha256 = replacementCheckpointSha256(snapshot);
        const immutableCheckpoint = immutableCheckpointSchema.parse({
            checkpoint_id: checkpointId,
            checkpoint_kind: 'local_immutable_outside_active_db',
            dataset_epoch: snapshot.profile.dataset_epoch,
            payload_sha256: payloadSha256,
            created_at: createdAt,
            immutable: true,
            validation_status: 'passed',
            retention_state: 'retain_until_replacement_verified',
        });
        if (immutableCheckpoint.checkpoint_kind !== 'local_immutable_outside_active_db') {
            throw new Error('local checkpoint parser returned an unexpected checkpoint kind');
        }
        const checkpoint = {
            ...immutableCheckpoint,
            checkpoint_kind: 'local_immutable_outside_active_db',
            local_profile_id: snapshot.profile.local_profile_id,
            source_database_name: this.database.name,
            snapshot_sha256: payloadSha256,
            snapshot,
        };
        await this.directory.replacement_checkpoints.add(checkpoint);
        const { snapshot: checkpointSnapshot, ...evidence } = checkpoint;
        void checkpointSnapshot;
        return structuredClone(evidence);
    }
    async replaceLocalDatasetEpoch(input) {
        await this.#replaceLocalDatasetEpoch(input);
    }
    async #replaceLocalDatasetEpoch(input) {
        this.assertOpen();
        const sourcePayload = parseLocalReplacementSourcePayload(input.source_payload);
        const currentRuntime = await this.requireRuntimeState();
        const currentEpoch = currentDatasetEpoch(this.profileValue, currentRuntime);
        const requestedEpoch = datasetEpochSchema.parse(input.dataset_epoch);
        const routeDraftWireGeneration = routeDraftWireGenerationOf(currentEpoch) ??
            routeDraftWireGenerationOf(sourcePayload.dataset_epoch);
        if (routeDraftWireGenerationOf(requestedEpoch) !== null &&
            routeDraftWireGenerationOf(requestedEpoch) !== routeDraftWireGeneration) {
            throw new Error('replacement route draft wire generation must derive from the current or source dataset');
        }
        const epoch = datasetEpochSchema.parse({
            ...requestedEpoch,
            ...(routeDraftWireGeneration === null
                ? {}
                : { route_draft_wire_generation: routeDraftWireGeneration }),
        });
        if (epoch.reason !== 'authoritative_replace' ||
            epoch.predecessor_epoch !== this.profileValue.dataset_epoch) {
            throw new DatasetEpochMismatchError(this.profileValue.dataset_epoch, epoch.predecessor_epoch ?? epoch.dataset_epoch);
        }
        if (epoch.restore_checkpoint_id === null) {
            throw new Error('authoritative replacement requires a matching immutable checkpoint');
        }
        if (epoch.dataset_epoch === sourcePayload.dataset_epoch.dataset_epoch) {
            throw new Error('authoritative replacement must not reuse the imported dataset epoch');
        }
        const authorization = validateLocalReplacementAuthorization(input.authorization, this.profileValue, epoch);
        const rebasedPayloads = exportPayloadEntities(sourcePayload).map((payload) => rebaseEntityPayload(payload, epoch.dataset_epoch));
        const rebasedTripRevisions = sourcePayload.trip_revisions.map((revision) => rebaseTripRevision(revision, epoch.dataset_epoch));
        const rebasedShifts = rebasedPayloads.flatMap((payload) => payload.entity_type === 'shift_aggregate' ? [payload.value] : []);
        validateTripRevisionReferences(rebasedShifts, rebasedTripRevisions);
        const rows = rebasedPayloads.map((payload) => entityRowFromPayload(payload, epoch.dataset_epoch));
        validateReplacementEntityRows(rows, epoch.dataset_epoch);
        const now = instantSchema.parse(this.runtime.now());
        if (compareInstants(epoch.created_at, now) > 0) {
            throw new Error('replacement dataset epoch cannot be created in the future');
        }
        const beforeSnapshot = await this.database.transaction('r', [
            this.database.entities,
            this.database.outbox,
            this.database.drafts,
            this.database.profiles,
            this.database.runtime_state,
            this.database.device_cursors,
            this.database.trip_revisions,
        ], async () => this.readReplacementCheckpointSnapshot());
        const beforeSnapshotSha256 = replacementCheckpointSha256(beforeSnapshot);
        const checkpoint = await this.directory.replacement_checkpoints.get(authorization.restore_checkpoint.checkpoint_id);
        const storedCheckpointSnapshotSha256 = checkpoint === undefined ? null : replacementCheckpointSha256(checkpoint.snapshot);
        if (checkpoint === undefined ||
            checkpoint.local_profile_id !== this.profileValue.local_profile_id ||
            checkpoint.dataset_epoch !== this.profileValue.dataset_epoch ||
            checkpoint.source_database_name !== this.database.name ||
            checkpoint.checkpoint_kind !== 'local_immutable_outside_active_db' ||
            checkpoint.snapshot_sha256 !== checkpoint.payload_sha256 ||
            storedCheckpointSnapshotSha256 !== checkpoint.snapshot_sha256 ||
            storedCheckpointSnapshotSha256 !== checkpoint.payload_sha256 ||
            !sameImmutableCheckpoint(checkpoint, authorization.restore_checkpoint)) {
            throw new Error('local replacement requires the exact immutable local checkpoint');
        }
        if (checkpoint.snapshot_sha256 !== beforeSnapshotSha256) {
            throw new Error('active local data changed after the replacement checkpoint');
        }
        const replacementId = uuidSchema.parse(authorization.restore_checkpoint.checkpoint_id);
        const execution = {
            replacement_id: replacementId,
            local_profile_id: this.profileValue.local_profile_id,
            execution_scope: authorization.execution_scope,
            connectivity: authorization.connectivity,
            account_identity_sha256: null,
            source_database_name: this.database.name,
            source_generation: this.directoryEntry.generation,
            predecessor_epoch: this.profileValue.dataset_epoch,
            replacement_epoch: epoch.dataset_epoch,
            restore_checkpoint_id: authorization.restore_checkpoint.checkpoint_id,
            restore_checkpoint_kind: authorization.restore_checkpoint.checkpoint_kind,
            restore_checkpoint_payload_sha256: authorization.restore_checkpoint.payload_sha256,
            invalidate_previous_outbox: true,
            force_other_devices_full_resync: authorization.force_other_devices_full_resync,
            before_snapshot_sha256: beforeSnapshotSha256,
            replacement_payload_sha256: replacementPayloadSha256(rows, rebasedTripRevisions, routeDraftWireGeneration ?? null),
            prepared_at: now,
            applied_at: null,
            status: 'prepared',
        };
        const preparedExecution = await prepareReplacementExecution(this.directory, this.directoryEntry, execution);
        this.runtime.fault?.('after_replacement_prepared');
        const replacementProfile = await this.database.transaction('rw', [
            this.database.entities,
            this.database.outbox,
            this.database.drafts,
            this.database.trip_revisions,
            this.database.device_cursors,
            this.database.profiles,
            this.database.runtime_state,
        ], async () => {
            const currentSnapshot = await this.readReplacementCheckpointSnapshot();
            if (replacementCheckpointSha256(currentSnapshot) !== beforeSnapshotSha256) {
                throw new Error('active local data changed after the replacement checkpoint');
            }
            const storedProfile = currentSnapshot.profile;
            const runtime = currentSnapshot.runtime_state;
            const profile = localProfileSchema.parse({
                ...storedProfile,
                dataset_epoch: epoch.dataset_epoch,
                last_mutation_at: now,
                last_successful_sync_at: null,
                last_verified_export: null,
            });
            await this.database.entities.clear();
            await this.database.outbox.clear();
            await this.database.drafts.clear();
            await this.database.trip_revisions.clear();
            await this.database.device_cursors.clear();
            await this.database.entities.bulkPut(rows);
            await this.database.trip_revisions.bulkAdd(rebasedTripRevisions);
            await this.database.profiles.put({ key: 'profile', value: profile });
            await this.database.runtime_state.put({
                ...runtime,
                dataset_epoch: epoch.dataset_epoch,
                dataset_epoch_record: epoch,
                next_local_sequence: '1',
                mutation_generation: nextSnapshotMutationGeneration(runtime),
                protected_mutation_generation: null,
                replacement_commit: {
                    replacement_id: replacementId,
                    execution_scope: authorization.execution_scope,
                    predecessor_epoch: storedProfile.dataset_epoch,
                    replacement_epoch: epoch.dataset_epoch,
                    restore_checkpoint_id: authorization.restore_checkpoint.checkpoint_id,
                    restore_checkpoint_payload_sha256: authorization.restore_checkpoint.payload_sha256,
                    committed_at: now,
                },
                preferences_initialized: true,
            });
            this.runtime.fault?.('before_replacement_postwrite_validation');
            const writtenRows = (await this.database.entities.toArray()).map((row) => parseEntityRowEnvelope(row, epoch.dataset_epoch));
            const writtenTripRevisions = (await this.database.trip_revisions.toArray()).map((revision) => tripRevisionSchema.parse(revision));
            if (replacementPayloadSha256(writtenRows, writtenTripRevisions, routeDraftWireGeneration ?? null) !== preparedExecution.replacement_payload_sha256) {
                throw new Error('authoritative replacement failed exact post-write validation');
            }
            return profile;
        });
        this.profileValue = replacementProfile;
        try {
            this.runtime.fault?.('after_replacement_database_commit');
        }
        catch (cause) {
            this.close();
            throw new LocalReplacementCommittedError(epoch.dataset_epoch, authorization.restore_checkpoint.checkpoint_id, { cause });
        }
        try {
            this.runtime.fault?.('before_replacement_directory_finalize');
            this.directoryEntry = await finalizeReplacementDirectory(this.directory, this.directoryEntry, preparedExecution, now);
        }
        catch (firstCause) {
            try {
                this.directoryEntry = await finalizeReplacementDirectory(this.directory, this.directoryEntry, preparedExecution, now);
            }
            catch (retryCause) {
                this.close();
                throw new LocalReplacementCommittedError(epoch.dataset_epoch, authorization.restore_checkpoint.checkpoint_id, { cause: new AggregateError([firstCause, retryCause], 'directory finalization failed') });
            }
        }
    }
    async statistics() {
        return derivePerformanceStats(await this.listShiftAggregates());
    }
    async lockProfile() {
        this.assertOpen();
        if (this.profileValue.account_identity === null) {
            throw new UnsupportedStructuralMutationError('anonymous profile cannot be locked');
        }
        const now = instantSchema.parse(this.runtime.now());
        const lockedProfile = await this.database.transaction('rw', [this.database.profiles, this.database.runtime_state], async () => {
            const storedProfile = await this.requireActiveStoredProfile();
            const runtime = await this.requireRuntimeState();
            const profile = localProfileSchema.parse({
                ...storedProfile,
                profile_state: 'locked',
            });
            await this.database.profiles.put({ key: 'profile', value: profile });
            await this.database.runtime_state.put({
                ...runtime,
                mutation_generation: nextSnapshotMutationGeneration(runtime),
            });
            return profile;
        });
        this.profileValue = lockedProfile;
        const lockedDirectoryEntry = {
            ...this.directoryEntry,
            profile_state: 'locked',
            updated_at: now,
        };
        try {
            await this.directory.profiles.put(lockedDirectoryEntry);
            this.directoryEntry = lockedDirectoryEntry;
        }
        finally {
            this.close();
        }
    }
    close() {
        if (this.#closed) {
            return;
        }
        this.database.close();
        this.directory.close();
        this.#closed = true;
    }
    async persistShiftMutation(mutation, options, committedDraft) {
        this.assertOpen();
        this.assertEpoch(mutation.dataset_epoch);
        const shiftId = uuidSchema.parse(mutation.shift.shift_id);
        const operationId = uuidSchema.parse(this.runtime.uuid());
        const now = instantSchema.parse(this.runtime.now());
        const result = await this.database.transaction('rw', [
            this.database.entities,
            this.database.outbox,
            this.database.runtime_state,
            this.database.profiles,
            this.database.drafts,
            this.database.trip_revisions,
            this.database.device_cursors,
        ], async () => {
            let draftToCommit = null;
            if (committedDraft !== null) {
                const draft = await this.database.drafts
                    .where('[local_profile_id+draft_key]')
                    .equals([this.profileValue.local_profile_id, committedDraft.draft_key])
                    .first();
                this.assertDraftVersion(committedDraft.draft_key, committedDraft.expected_version, draft === undefined ? null : draftVersion(draft));
                if (draft === undefined) {
                    throw new Error('draft CAS accepted an absent draft');
                }
                this.assertEpoch(draft.dataset_epoch);
                draftToCommit = draft;
            }
            const storedProfile = await this.assertWriteAllowed();
            const runtime = await this.requireRuntimeState();
            const existingRow = await this.database.entities.get(entityTableKey('shift_aggregate', shiftId));
            if (existingRow !== undefined) {
                assertRowEpoch(existingRow, storedProfile.dataset_epoch);
                if (existingRow.payload.entity_type !== 'shift_aggregate' ||
                    existingRow.entity_id !== shiftId) {
                    throw new UnsupportedStructuralMutationError('shift aggregate storage identity does not match its canonical payload');
                }
            }
            const existing = existingRow?.payload.entity_type === 'shift_aggregate' ? existingRow.payload.value : null;
            this.assertExpectedRevision(shiftId, options.expected_revision, existing?.aggregate_revision ?? null);
            if (existing?.shift.metadata.tombstone) {
                throw new UnsupportedStructuralMutationError('soft-deleted shifts must use the explicit restore operation');
            }
            const aggregate = normalizeShiftMutation(mutation, existing, now, expandedRouteDraftWireActive(runtime));
            const operationKind = 'upsert';
            const profile = await this.writeAggregateAndRouteLearning(aggregate, existing, operationKind, options.operation_origin ?? 'local_edit', operationId, now, storedProfile, runtime);
            if (draftToCommit !== null) {
                await this.database.drafts.delete(draftToCommit.draft_id);
                this.runtime.fault?.('after_draft_delete');
            }
            return { aggregate, profile };
        });
        this.profileValue = result.profile;
        return structuredClone(result.aggregate);
    }
    async writeAggregateAndRouteLearning(aggregate, previousAggregate, operationKind, operationOrigin, operationId, createdAt, storedProfile, runtime, tripRevisionOperationKind = operationKind) {
        const affectedRouteIds = changedRouteIds(previousAggregate, aggregate);
        const aggregatesAfterMutation = await collectShiftAggregatesReplacing(this.database, storedProfile.dataset_epoch, aggregate);
        const routeChanges = await recomputeChangedLiveRoutes(this.database, storedProfile.dataset_epoch, affectedRouteIds, aggregatesAfterMutation, createdAt, expandedRouteDraftWireActive(runtime));
        const payloads = [
            ...routeChanges.map(({ value }) => ({
                entity_type: 'route_variant',
                value,
            })),
            { entity_type: 'shift_aggregate', value: aggregate },
        ];
        const operationIds = [
            ...routeChanges.map(() => uuidSchema.parse(this.runtime.uuid())),
            operationId,
        ];
        const baseRevisions = [
            ...routeChanges.map(({ previous }) => previous.metadata.revision),
            previousAggregate?.aggregate_revision ?? null,
        ];
        const operations = payloads.map((payload, index) => {
            const payloadOperationId = operationIds[index];
            const baseRevision = baseRevisions[index];
            if (payloadOperationId === undefined || baseRevision === undefined) {
                throw new Error('aggregate route-learning operation projection is incomplete');
            }
            return makePendingOperation({
                operation_id: payloadOperationId,
                device_id: runtime.device_id,
                local_profile_id: storedProfile.local_profile_id,
                dataset_epoch: runtime.dataset_epoch,
                local_sequence: (BigInt(runtime.next_local_sequence) + BigInt(index)).toString(),
                operation_kind: payload.entity_type === 'shift_aggregate' ? operationKind : 'upsert',
                operation_origin: operationOrigin,
                base_revision: baseRevision,
                payload,
                created_at: createdAt,
            });
        });
        await this.database.entities.bulkPut(payloads.map((payload) => entityRowFromPayload(payload, runtime.dataset_epoch)));
        await this.writeTripRevisionDelta(previousAggregate, aggregate, tripRevisionOperationKind, runtime.device_id, createdAt);
        this.runtime.fault?.('after_domain_write');
        this.runtime.fault?.('before_outbox_write');
        await this.database.outbox.bulkAdd(operations);
        await this.database.runtime_state.put({
            ...runtime,
            next_local_sequence: (BigInt(runtime.next_local_sequence) + BigInt(operations.length)).toString(),
            mutation_generation: nextSnapshotMutationGeneration(runtime),
        });
        const updatedProfile = localProfileSchema.parse({
            ...storedProfile,
            last_mutation_at: createdAt,
        });
        await this.database.profiles.put({ key: 'profile', value: updatedProfile });
        return updatedProfile;
    }
    async writeShiftLifecycleAndRouteLearning(aggregate, previousAggregate, operationKind, shiftOperationId, createdAt, storedProfile, runtime) {
        const affectedRouteIds = aggregate.segments
            .flatMap((segment) => segment.route_snapshot === null ? [] : [segment.route_snapshot.route_variant_id])
            .filter((routeId, index, routeIds) => routeIds.indexOf(routeId) === index)
            .sort();
        const aggregatesAfterMutation = await collectShiftAggregatesReplacing(this.database, storedProfile.dataset_epoch, aggregate);
        const routeChanges = await recomputeChangedLiveRoutes(this.database, storedProfile.dataset_epoch, affectedRouteIds, aggregatesAfterMutation, createdAt, expandedRouteDraftWireActive(runtime));
        const payloads = [
            ...routeChanges.map(({ value }) => ({
                entity_type: 'route_variant',
                value,
            })),
            { entity_type: 'shift_aggregate', value: aggregate },
        ];
        const operationIds = [
            ...routeChanges.map(() => uuidSchema.parse(this.runtime.uuid())),
            shiftOperationId,
        ];
        const baseRevisions = [
            ...routeChanges.map(({ previous }) => previous.metadata.revision),
            previousAggregate.aggregate_revision,
        ];
        const operations = payloads.map((payload, index) => {
            const operationId = operationIds[index];
            const baseRevision = baseRevisions[index];
            if (operationId === undefined || baseRevision === undefined) {
                throw new Error('shift lifecycle route operation projection is incomplete');
            }
            return makePendingOperation({
                operation_id: operationId,
                device_id: runtime.device_id,
                local_profile_id: storedProfile.local_profile_id,
                dataset_epoch: runtime.dataset_epoch,
                local_sequence: (BigInt(runtime.next_local_sequence) + BigInt(index)).toString(),
                operation_origin: 'local_edit',
                base_revision: baseRevision,
                payload,
                created_at: createdAt,
                operation_kind: payload.entity_type === 'shift_aggregate' ? operationKind : 'upsert',
            });
        });
        await this.database.entities.bulkPut(payloads.map((payload) => entityRowFromPayload(payload, runtime.dataset_epoch)));
        await this.writeTripRevisionDelta(previousAggregate, aggregate, operationKind, runtime.device_id, createdAt);
        this.runtime.fault?.('after_domain_write');
        this.runtime.fault?.('before_outbox_write');
        await this.database.outbox.bulkAdd(operations);
        await this.database.runtime_state.put({
            ...runtime,
            next_local_sequence: (BigInt(runtime.next_local_sequence) + BigInt(operations.length)).toString(),
            mutation_generation: nextSnapshotMutationGeneration(runtime),
        });
        const updatedProfile = localProfileSchema.parse({
            ...storedProfile,
            last_mutation_at: createdAt,
        });
        await this.database.profiles.put({ key: 'profile', value: updatedProfile });
        return updatedProfile;
    }
    async writeTripRevisionDelta(previousAggregate, aggregate, operationKind, actorDeviceId, recordedAt) {
        const previousById = new Map(previousAggregate?.segments.map((segment) => [segment.trip_segment_id, segment]) ?? []);
        const revisions = [];
        for (const segment of aggregate.segments) {
            const before = previousById.get(segment.trip_segment_id) ?? null;
            if (before !== null && before.metadata.revision === segment.metadata.revision) {
                continue;
            }
            revisions.push(tripRevisionSchema.parse({
                trip_revision_id: uuidSchema.parse(this.runtime.uuid()),
                shift_id: aggregate.shift.shift_id,
                trip_segment_id: segment.trip_segment_id,
                dataset_epoch: aggregate.dataset_epoch,
                change_kind: segmentChangeKind(before, segment, operationKind),
                previous_revision: before?.metadata.revision ?? null,
                resulting_revision: segment.metadata.revision,
                recorded_at: recordedAt,
                actor_device_id: actorDeviceId,
                before,
                after: segment,
            }));
        }
        if (revisions.length > 0) {
            await this.database.trip_revisions.bulkAdd(revisions);
        }
    }
    async requireShift(shiftId, expectedRevision = 0) {
        const row = await this.database.entities.get(entityTableKey('shift_aggregate', shiftId));
        if (row?.payload.entity_type !== 'shift_aggregate') {
            throw new RevisionConflictError(shiftId, expectedRevision, null);
        }
        this.assertEpoch(row.dataset_epoch);
        return row.payload.value;
    }
    async assertUniqueLiveRouteFingerprint(routeVariant, previousRoute) {
        const fingerprint = routeVariantFingerprint(routeVariant);
        if (previousRoute !== null) {
            if (routeVariantFingerprint(previousRoute) !== fingerprint) {
                throw new UnsupportedStructuralMutationError('an existing route variant identity cannot be changed while recording a new use');
            }
            // Deployed schema-3 clients could create more than one live UUID for the
            // same semantic fingerprint. Reusing one such identity does not create a
            // new duplicate and must remain a supported in-place upgrade path.
            return;
        }
        const routeRows = await this.database.entities
            .where('entity_type')
            .equals('route_variant')
            .toArray();
        for (const row of routeRows) {
            this.assertEpoch(row.dataset_epoch);
            if (row.payload.entity_type === 'route_variant' &&
                row.payload.value.metadata.tombstone === null) {
                const existing = routeVariantSchema.parse(row.payload.value);
                if (existing.route_variant_id !== routeVariant.route_variant_id &&
                    routeVariantFingerprint(existing) === fingerprint) {
                    throw new UnsupportedStructuralMutationError(`live route fingerprint already belongs to ${existing.route_variant_id}`);
                }
            }
        }
    }
    async readReplacementCheckpointSnapshot() {
        const profile = await this.requireActiveStoredProfile();
        const runtime = await this.requireRuntimeState();
        const entities = (await this.database.entities.toArray()).sort((left, right) => `${left.entity_type}\u0000${left.entity_id}`.localeCompare(`${right.entity_type}\u0000${right.entity_id}`));
        const pendingOutbox = (await this.database.outbox.toArray()).sort((left, right) => compareDecimalCursors(left.local_sequence, right.local_sequence) ||
            left.operation_id.localeCompare(right.operation_id));
        const drafts = (await this.database.drafts.toArray()).sort((left, right) => left.draft_id.localeCompare(right.draft_id));
        const deviceCursors = (await this.database.device_cursors.toArray()).sort((left, right) => left.device_id.localeCompare(right.device_id));
        const tripRevisions = (await this.database.trip_revisions.toArray()).sort((left, right) => left.trip_revision_id.localeCompare(right.trip_revision_id));
        return {
            profile: structuredClone(profile),
            runtime_state: structuredClone(runtime),
            entities: structuredClone(entities),
            pending_outbox: structuredClone(pendingOutbox),
            drafts: structuredClone(drafts),
            device_cursors: structuredClone(deviceCursors),
            trip_revisions: structuredClone(tripRevisions),
        };
    }
    async readConsistently(targetTables, query) {
        return this.database.transaction('r', [this.database.profiles, this.database.runtime_state, ...targetTables], async () => {
            const storedProfile = await this.requireReadableStoredProfile();
            this.runtime.fault?.('after_read_guard');
            if (this.runtime.pause !== undefined) {
                const pause = this.runtime.pause('after_read_guard');
                if (pause !== undefined) {
                    await Dexie.waitFor(pause);
                }
            }
            return query(storedProfile);
        });
    }
    async requireRuntimeState() {
        const runtime = await this.database.runtime_state.get('runtime');
        if (runtime === undefined || runtime.local_profile_id !== this.profileValue.local_profile_id) {
            throw new Error('local database runtime state is missing or belongs to another profile');
        }
        if (runtime.write_fence !== 'open' ||
            runtime.physical_database_name !== this.database.name ||
            runtime.mutation_generation === undefined ||
            runtime.protected_mutation_generation === undefined) {
            throw new FullResyncRequiredError('this local database generation is no longer writable');
        }
        decimalCursorSchema.parse(runtime.mutation_generation);
        if (runtime.protected_mutation_generation !== null) {
            decimalCursorSchema.parse(runtime.protected_mutation_generation);
        }
        return runtime;
    }
    async requireActiveStoredProfile() {
        const row = await this.database.profiles.get('profile');
        if (row === undefined) {
            throw new ProfileAccessDeniedError('complete_wipe');
        }
        const storedProfile = localProfileSchema.parse(row.value);
        if (storedProfile.local_profile_id !== this.profileValue.local_profile_id ||
            storedProfile.database_binding.database_name !== this.logical_database_name) {
            throw new ProfileAccessDeniedError('complete_wipe');
        }
        if (storedProfile.profile_state !== 'active') {
            throw new ProfileAccessDeniedError(storedProfile.profile_state === 'locked' ? 'authenticate_same_account' : 'complete_wipe');
        }
        if (storedProfile.dataset_epoch !== this.profileValue.dataset_epoch) {
            throw new DatasetEpochMismatchError(storedProfile.dataset_epoch, this.profileValue.dataset_epoch);
        }
        return storedProfile;
    }
    async requireReadableStoredProfile() {
        const storedProfile = await this.requireActiveStoredProfile();
        const runtime = await this.requireRuntimeState();
        if (runtime.dataset_epoch !== storedProfile.dataset_epoch) {
            throw new DatasetEpochMismatchError(storedProfile.dataset_epoch, runtime.dataset_epoch);
        }
        return storedProfile;
    }
    async assertWriteAllowed() {
        if (this.profileValue.profile_state !== 'active') {
            throw new ProfileAccessDeniedError(this.profileValue.profile_state === 'locked'
                ? 'authenticate_same_account'
                : 'complete_wipe');
        }
        const storedProfile = await this.requireActiveStoredProfile();
        const runtime = await this.requireRuntimeState();
        if (runtime.dataset_epoch !== storedProfile.dataset_epoch) {
            throw new DatasetEpochMismatchError(storedProfile.dataset_epoch, runtime.dataset_epoch);
        }
        const staleOperation = (await this.database.outbox.toArray()).find((operation) => operation.dataset_epoch !== runtime.dataset_epoch);
        if (staleOperation) {
            throw new DatasetEpochMismatchError(runtime.dataset_epoch, staleOperation.dataset_epoch);
        }
        if (this.profileValue.account_identity === null) {
            return storedProfile;
        }
        const cursor = await this.database.device_cursors.get(runtime.device_id);
        if (cursor === undefined ||
            !canDeviceWrite(cursor, runtime.dataset_epoch, instantSchema.parse(this.runtime.now()))) {
            throw new FullResyncRequiredError();
        }
        return storedProfile;
    }
    assertExpectedRevision(entityId, expectedRevision, actualRevision) {
        if (expectedRevision !== actualRevision) {
            throw new RevisionConflictError(entityId, expectedRevision, actualRevision);
        }
    }
    assertDraftVersion(draftKey, expectedVersionInput, actualVersion) {
        const expectedVersion = expectedVersionInput === null ? null : parseDraftVersionToken(expectedVersionInput);
        if (expectedVersion?.draft_id !== actualVersion?.draft_id ||
            expectedVersion?.autosave_revision !== actualVersion?.autosave_revision) {
            throw new DraftRevisionConflictError(draftKey, expectedVersion, actualVersion);
        }
    }
    assertEpoch(receivedEpoch) {
        if (receivedEpoch !== this.profileValue.dataset_epoch) {
            throw new DatasetEpochMismatchError(this.profileValue.dataset_epoch, receivedEpoch);
        }
    }
    assertOpen() {
        if (this.#closed) {
            throw new RepositoryClosedError();
        }
    }
}
function currentDatasetEpoch(profile, runtime) {
    const marker = runtime.replacement_commit ?? null;
    const record = datasetEpochSchema.parse(runtime.dataset_epoch_record ??
        (marker === null
            ? {
                dataset_epoch: profile.dataset_epoch,
                created_at: profile.created_at,
                predecessor_epoch: null,
                reason: 'initial',
                restore_checkpoint_id: null,
            }
            : {
                dataset_epoch: marker.replacement_epoch,
                created_at: marker.committed_at,
                predecessor_epoch: marker.predecessor_epoch,
                reason: 'authoritative_replace',
                restore_checkpoint_id: marker.restore_checkpoint_id,
            }));
    if (record.dataset_epoch !== profile.dataset_epoch ||
        record.dataset_epoch !== runtime.dataset_epoch) {
        throw new DatasetEpochMismatchError(profile.dataset_epoch, record.dataset_epoch);
    }
    if (marker === null) {
        if (record.reason !== 'initial') {
            throw new FullResyncRequiredError('replacement dataset epoch provenance is missing its durable commit marker');
        }
        return record;
    }
    if (record.reason !== 'authoritative_replace' ||
        record.predecessor_epoch !== marker.predecessor_epoch ||
        record.restore_checkpoint_id !== marker.restore_checkpoint_id ||
        marker.replacement_epoch !== record.dataset_epoch ||
        compareInstants(record.created_at, marker.committed_at) > 0) {
        throw new FullResyncRequiredError('dataset epoch record differs from its durable replacement provenance');
    }
    return record;
}
function expandedRouteDraftWireActive(runtime) {
    if (runtime.dataset_epoch_record === undefined) {
        throw new FullResyncRequiredError('route draft wire generation requires durable dataset epoch provenance');
    }
    const epoch = datasetEpochSchema.parse(runtime.dataset_epoch_record);
    if (epoch.dataset_epoch !== runtime.dataset_epoch) {
        throw new FullResyncRequiredError('route draft wire generation belongs to another epoch');
    }
    return routeDraftWireGenerationOf(epoch) === EXPANDED_ROUTE_DRAFT_WIRE_GENERATION;
}
function exportPayloadEntities(payload) {
    return [
        ...payload.shifts.map((value) => ({
            entity_type: 'shift_aggregate',
            value,
        })),
        ...payload.vehicles.map((value) => ({ entity_type: 'vehicle', value })),
        ...payload.route_variants.map((value) => ({
            entity_type: 'route_variant',
            value,
        })),
        ...payload.route_templates.map((value) => ({
            entity_type: 'route_template',
            value,
        })),
        ...payload.user_defined_locations.map((value) => ({
            entity_type: 'user_defined_location',
            value,
        })),
        ...payload.location_merge_decisions.map((value) => ({
            entity_type: 'location_merge_decision',
            value,
        })),
        { entity_type: 'user_preferences', value: payload.user_preferences },
    ];
}
function parseLocalReplacementSourcePayload(sourcePayloadInput) {
    const source = exportPayloadSchema.parse(sourcePayloadInput);
    const expandedWire = routeDraftWireGenerationOf(source.dataset_epoch) === EXPANDED_ROUTE_DRAFT_WIRE_GENERATION;
    const imported = normalizeImportedRouteLearning(source, expandedWire);
    const payload = ROUTE_DRAFT_WIRE_WRITE_MODE === 'compatibility_reader' && !expandedWire
        ? exportPayloadSchema.parse({
            ...imported,
            shifts: imported.shifts.map(projectShiftAggregateForProductionA6Rollback),
        })
        : imported;
    validateTripRevisionReferences(payload.shifts, payload.trip_revisions);
    const entities = exportPayloadEntities(payload);
    validateReplacementEntityRows(entities.map((entity) => entityRowFromPayload(entity, payload.dataset_epoch.dataset_epoch)), payload.dataset_epoch.dataset_epoch);
    validateImportedRouteLearning(entities, expandedWire);
    return payload;
}
function normalizeImportedRouteLearning(payload, expandedWire) {
    const normalizedRoutes = payload.route_variants.map((route) => {
        if (route.metadata.tombstone !== null) {
            return routeVariantForCurrentWriteMode(route, expandedWire);
        }
        const normalized = recomputeRouteLearning(route, payload.shifts, route.metadata.updated_at, route, expandedWire);
        return routeVariantForCurrentWriteMode({ ...normalized, metadata: route.metadata }, expandedWire);
    });
    return exportPayloadSchema.parse({ ...payload, route_variants: normalizedRoutes });
}
function validateReplacementEntityRows(rows, expectedDatasetEpoch) {
    if (new Set(rows.map((row) => `${row.entity_type}\u0000${row.entity_id}`)).size !== rows.length) {
        throw new Error('replacement dataset contains duplicate entity identities');
    }
    for (const row of rows) {
        parseEntityRowEnvelope(row, expectedDatasetEpoch);
        if (row.payload.entity_type === 'shift_aggregate' &&
            row.payload.value.dataset_epoch !== expectedDatasetEpoch) {
            throw new DatasetEpochMismatchError(expectedDatasetEpoch, row.payload.value.dataset_epoch);
        }
        const tombstone = rootTombstone(row.payload);
        if (tombstone !== null && tombstone.dataset_epoch !== expectedDatasetEpoch) {
            throw new DatasetEpochMismatchError(expectedDatasetEpoch, tombstone.dataset_epoch);
        }
    }
}
function rebaseEntityPayload(payload, datasetEpoch) {
    switch (payload.entity_type) {
        case 'shift_aggregate':
            return syncEntityPayloadSchema.parse({
                entity_type: payload.entity_type,
                value: {
                    ...payload.value,
                    dataset_epoch: datasetEpoch,
                    shift: {
                        ...payload.value.shift,
                        metadata: rebaseMetadata(payload.value.shift.metadata, datasetEpoch),
                    },
                    segments: payload.value.segments.map((segment) => rebaseTripSegment(segment, datasetEpoch)),
                },
            });
        case 'vehicle':
        case 'route_variant':
        case 'route_template':
        case 'user_defined_location':
        case 'location_merge_decision':
        case 'user_preferences':
            return syncEntityPayloadSchema.parse({
                entity_type: payload.entity_type,
                value: {
                    ...payload.value,
                    metadata: rebaseMetadata(payload.value.metadata, datasetEpoch),
                },
            });
    }
}
function rebaseMetadata(metadata, datasetEpoch) {
    return {
        ...metadata,
        tombstone: metadata.tombstone === null ? null : { ...metadata.tombstone, dataset_epoch: datasetEpoch },
    };
}
function rebaseTripSegment(segment, datasetEpoch) {
    return {
        ...segment,
        metadata: rebaseMetadata(segment.metadata, datasetEpoch),
    };
}
function rebaseTripRevision(revision, datasetEpoch) {
    return tripRevisionSchema.parse({
        ...revision,
        dataset_epoch: datasetEpoch,
        before: revision.before === null ? null : rebaseTripSegment(revision.before, datasetEpoch),
        after: revision.after === null ? null : rebaseTripSegment(revision.after, datasetEpoch),
    });
}
function validateImportedRouteLearning(payloads, expandedWire) {
    const shifts = payloads.flatMap((payload) => payload.entity_type === 'shift_aggregate' ? [payload.value] : []);
    const routes = payloads.flatMap((payload) => payload.entity_type === 'route_variant' ? [payload.value] : []);
    const routesById = new Map(routes.map((route) => [route.route_variant_id, route]));
    const liveRoutes = new Map(routes
        .filter((route) => route.metadata.tombstone === null)
        .map((route) => [route.route_variant_id, route]));
    const referencedRouteIds = new Set(shifts.flatMap((aggregate) => aggregate.shift.metadata.tombstone !== null
        ? []
        : aggregate.segments.flatMap((segment) => segment.metadata.tombstone !== null ||
            segment.route_snapshot === null ||
            (segment.status !== 'complete' && segment.status !== 'partial')
            ? []
            : [segment.route_snapshot.route_variant_id])));
    // Legacy exports may contain several live route UUIDs for one fingerprint.
    // Keep every historical identity and snapshot intact; current writes prevent
    // new duplicates, while the composer deterministically treats these rows as
    // aliases of one semantic route.
    for (const routeId of referencedRouteIds) {
        const route = routesById.get(routeId);
        if (route !== undefined && route.metadata.tombstone !== null) {
            throw new UnsupportedStructuralMutationError(`replacement live route snapshot ${routeId} references a deleted learned route variant`);
        }
    }
    for (const route of liveRoutes.values()) {
        const recomputed = recomputeRouteLearning(route, shifts, route.metadata.updated_at, route, expandedWire);
        if (!routeVariantLearningMatches(route, recomputed)) {
            throw new UnsupportedStructuralMutationError(`replacement route learning ${route.route_variant_id} differs from its live snapshots`);
        }
    }
}
function validateTripRevisionReferences(shifts, revisions) {
    const shiftsById = new Map(shifts.map((aggregate) => [aggregate.shift.shift_id, aggregate]));
    if (shiftsById.size !== shifts.length) {
        throw new Error('trip revision validation requires unique shift identities');
    }
    const segmentOwners = new Map();
    for (const aggregate of shifts) {
        for (const segment of aggregate.segments) {
            const owner = segmentOwners.get(segment.trip_segment_id);
            if (owner !== undefined && owner !== aggregate.shift.shift_id) {
                throw new Error('a trip-segment identity cannot belong to multiple shifts');
            }
            segmentOwners.set(segment.trip_segment_id, aggregate.shift.shift_id);
        }
    }
    const chains = new Map();
    for (const revision of revisions) {
        const aggregate = shiftsById.get(revision.shift_id);
        if (aggregate === undefined) {
            throw new Error('trip revision references a missing shift');
        }
        const segment = aggregate.segments.find((candidate) => candidate.trip_segment_id === revision.trip_segment_id);
        if (segment === undefined) {
            throw new Error('trip revision references a missing shift segment');
        }
        const key = `${revision.shift_id}\u0000${revision.trip_segment_id}`;
        const chain = chains.get(key) ?? [];
        chain.push(revision);
        chains.set(key, chain);
    }
    for (const [key, chainInput] of chains) {
        const chain = [...chainInput].sort((left, right) => left.resulting_revision - right.resulting_revision ||
            compareInstants(left.recorded_at, right.recorded_at) ||
            left.trip_revision_id.localeCompare(right.trip_revision_id));
        if (new Set(chain.map((revision) => revision.resulting_revision)).size !== chain.length) {
            throw new Error('trip revision history contains duplicate resulting revisions');
        }
        for (let index = 0; index < chain.length; index += 1) {
            const revision = chain[index];
            if (revision === undefined) {
                throw new Error('trip revision chain projection is incomplete');
            }
            const previous = chain[index - 1];
            if (previous === undefined) {
                if (revision.previous_revision !== null ||
                    revision.resulting_revision !== 1 ||
                    revision.before !== null) {
                    throw new Error('complete trip revision history must begin with revision one creation');
                }
            }
            else {
                if (revision.previous_revision !== previous.resulting_revision ||
                    previous.after === null ||
                    revision.before === null ||
                    !sameJson(previous.after, revision.before) ||
                    compareInstants(revision.recorded_at, previous.recorded_at) < 0) {
                    throw new Error('trip revision history is not a contiguous canonical chain');
                }
            }
        }
        const [shiftId, segmentId] = key.split('\u0000');
        const current = shiftsById
            .get(shiftId ?? '')
            ?.segments.find((segment) => segment.trip_segment_id === segmentId);
        const latest = chain.at(-1);
        if (current === undefined ||
            latest?.after === null ||
            latest?.after === undefined ||
            !sameJson(latest.after, current)) {
            throw new Error('latest trip revision does not match the canonical shift segment');
        }
    }
    for (const [segmentId, shiftId] of segmentOwners) {
        if (!chains.has(`${shiftId}\u0000${segmentId}`)) {
            throw new Error('complete trip revision history is missing for a current shift segment');
        }
    }
}
function sameImmutableCheckpoint(stored, supplied) {
    return (stored.checkpoint_id === supplied.checkpoint_id &&
        stored.checkpoint_kind === supplied.checkpoint_kind &&
        stored.dataset_epoch === supplied.dataset_epoch &&
        stored.payload_sha256 === supplied.payload_sha256 &&
        stored.created_at === supplied.created_at &&
        stored.immutable === supplied.immutable &&
        stored.validation_status === supplied.validation_status &&
        stored.retention_state === supplied.retention_state);
}
function validateLocalReplacementAuthorization(authorizationInput, profile, epoch) {
    const checkpoint = immutableCheckpointSchema.parse(authorizationInput.restore_checkpoint);
    if (checkpoint.checkpoint_id !== epoch.restore_checkpoint_id ||
        checkpoint.dataset_epoch !== profile.dataset_epoch ||
        epoch.predecessor_epoch !== profile.dataset_epoch ||
        compareInstants(epoch.created_at, checkpoint.created_at) < 0) {
        throw new DatasetEpochMismatchError(profile.dataset_epoch, checkpoint.dataset_epoch);
    }
    if (authorizationInput.invalidate_previous_outbox !== true ||
        authorizationInput.target_account_binding === undefined ||
        authorizationInput.connectivity === undefined) {
        throw new Error('replacement authorization is incomplete');
    }
    if (authorizationInput.execution_scope !== 'local_profile' ||
        profile.account_identity !== null ||
        authorizationInput.target_account_binding !== 'unbound' ||
        checkpoint.checkpoint_kind !== 'local_immutable_outside_active_db' ||
        authorizationInput.force_other_devices_full_resync !== false ||
        (authorizationInput.connectivity !== 'offline' && authorizationInput.connectivity !== 'online')) {
        throw new UnsupportedStructuralMutationError('local replacement is permitted only for an unbound profile with a local checkpoint');
    }
    return { ...authorizationInput, restore_checkpoint: checkpoint };
}
async function prepareReplacementExecution(directory, expectedEntry, proposed) {
    return directory.transaction('rw', [directory.profiles, directory.replacement_executions], async () => {
        const currentEntry = await directory.profiles.get(proposed.local_profile_id);
        if (currentEntry === undefined ||
            currentEntry.active_database_name !== proposed.source_database_name ||
            currentEntry.generation !== proposed.source_generation ||
            currentEntry.dataset_epoch !== proposed.predecessor_epoch ||
            expectedEntry.local_profile_id !== proposed.local_profile_id) {
            throw new FullResyncRequiredError('profile directory changed before replacement preparation');
        }
        const existing = await directory.replacement_executions.get(proposed.replacement_id);
        if (existing !== undefined) {
            if (existing.status !== 'prepared' || !replacementPreparationsMatch(existing, proposed)) {
                throw new FullResyncRequiredError('replacement authorization ID already belongs to another execution');
            }
            return existing;
        }
        const competing = await directory.replacement_executions
            .where('[local_profile_id+replacement_epoch]')
            .equals([proposed.local_profile_id, proposed.replacement_epoch])
            .first();
        if (competing !== undefined) {
            throw new FullResyncRequiredError('another replacement is already prepared for this profile and epoch');
        }
        await directory.replacement_executions.add(proposed);
        return proposed;
    });
}
function replacementPreparationsMatch(left, right) {
    return (left.replacement_id === right.replacement_id &&
        left.local_profile_id === right.local_profile_id &&
        left.execution_scope === right.execution_scope &&
        left.connectivity === right.connectivity &&
        left.account_identity_sha256 === right.account_identity_sha256 &&
        left.source_database_name === right.source_database_name &&
        left.source_generation === right.source_generation &&
        left.predecessor_epoch === right.predecessor_epoch &&
        left.replacement_epoch === right.replacement_epoch &&
        left.restore_checkpoint_id === right.restore_checkpoint_id &&
        left.restore_checkpoint_kind === right.restore_checkpoint_kind &&
        left.restore_checkpoint_payload_sha256 === right.restore_checkpoint_payload_sha256 &&
        left.invalidate_previous_outbox === right.invalidate_previous_outbox &&
        left.force_other_devices_full_resync === right.force_other_devices_full_resync &&
        left.before_snapshot_sha256 === right.before_snapshot_sha256 &&
        left.replacement_payload_sha256 === right.replacement_payload_sha256);
}
export async function finalizeReplacementDirectory(directory, expectedEntry, execution, appliedAt) {
    return directory.transaction('rw', [directory.profiles, directory.replacement_executions], async () => {
        const currentEntry = await directory.profiles.get(execution.local_profile_id);
        const currentExecution = await directory.replacement_executions.get(execution.replacement_id);
        const preparedTransition = currentExecution?.status === 'prepared' &&
            currentExecution.applied_at === null &&
            currentEntry?.dataset_epoch === execution.predecessor_epoch;
        const appliedRetry = currentExecution?.status === 'applied' &&
            currentExecution.applied_at !== null &&
            currentEntry?.dataset_epoch === execution.replacement_epoch;
        if (currentEntry === undefined ||
            currentExecution === undefined ||
            currentEntry.active_database_name !== execution.source_database_name ||
            currentEntry.generation !== execution.source_generation ||
            !replacementExecutionImmutableFieldsMatch(currentExecution, execution) ||
            (!preparedTransition && !appliedRetry) ||
            expectedEntry.local_profile_id !== execution.local_profile_id) {
            throw new FullResyncRequiredError('replacement directory activation no longer matches its durable intent');
        }
        if (appliedRetry) {
            return currentEntry;
        }
        const activated = {
            ...currentEntry,
            dataset_epoch: execution.replacement_epoch,
            updated_at: appliedAt,
        };
        await directory.profiles.put(activated);
        await directory.replacement_executions.put({
            ...currentExecution,
            status: 'applied',
            applied_at: appliedAt,
        });
        return activated;
    });
}
function normalizeShiftMutation(mutation, existing, now, expandedWire) {
    const existingSegments = new Map(existing?.segments.map((segment) => [segment.trip_segment_id, segment]) ?? []);
    if (existing !== null) {
        const incomingIds = new Set(mutation.segments.map((segment) => segment.trip_segment_id));
        const removed = existing.segments.find((segment) => !incomingIds.has(segment.trip_segment_id));
        if (removed) {
            throw new UnsupportedStructuralMutationError('segment removal requires an explicit split/merge/delete repository operation');
        }
        const changedDeletedSegment = mutation.segments.find((segment) => {
            const previous = existingSegments.get(segment.trip_segment_id);
            return (previous !== undefined &&
                previous.metadata.tombstone !== null &&
                !sameJson(segmentForCurrentWriteMode(segment, expandedWire), withoutEntityMetadata(previous)));
        });
        if (changedDeletedSegment !== undefined) {
            throw new UnsupportedStructuralMutationError('soft-deleted segments require an explicit restore operation before mutation');
        }
    }
    assertCurrentRouteShape(mutation, existing, expandedWire);
    const shiftMetadata = nextMetadata(existing?.shift ?? null, mutation.shift, now);
    const segments = mutation.segments.map((segment) => {
        const previous = existingSegments.get(segment.trip_segment_id) ?? null;
        const projected = segmentForCurrentWriteMode(segment, expandedWire);
        return {
            ...projected,
            metadata: nextMetadata(previous, projected, now),
        };
    });
    const normalized = shiftAggregateSchema.parse({
        contract_version: mutation.contract_version,
        dataset_epoch: mutation.dataset_epoch,
        aggregate_revision: (existing?.aggregate_revision ?? 0) + 1,
        shift: { ...mutation.shift, metadata: shiftMetadata },
        segments,
    });
    if (ROUTE_DRAFT_WIRE_WRITE_MODE === 'compatibility_reader' && !expandedWire) {
        productionA6RollbackShiftAggregateSchema.parse(normalized);
    }
    return normalized;
}
function segmentForCurrentWriteMode(segment, expandedWire) {
    if (ROUTE_DRAFT_WIRE_WRITE_MODE !== 'compatibility_reader' ||
        expandedWire ||
        !Object.hasOwn(segment, 'route_draft_snapshot')) {
        return segment;
    }
    const routeDraftSnapshot = segment.route_draft_snapshot;
    if (routeDraftSnapshot === undefined) {
        throw new UnsupportedStructuralMutationError('the compatibility reader cannot accept an undefined route draft snapshot');
    }
    if (routeDraftSnapshot !== null &&
        !isEmptyRouteDraftSnapshotForProductionA6Rollback(routeDraftSnapshot)) {
        throw new UnsupportedStructuralMutationError('the compatibility reader cannot introduce a route draft snapshot');
    }
    const { route_draft_snapshot: omitted, ...rollbackSafe } = segment;
    void omitted;
    return rollbackSafe;
}
function assertCurrentRouteShape(mutation, existing, expandedWire) {
    const existingSegments = new Map(existing?.segments.map((segment) => [segment.trip_segment_id, segment]) ?? []);
    if (ROUTE_DRAFT_WIRE_WRITE_MODE === 'compatibility_reader' &&
        !expandedWire &&
        existing?.segments.some((segment) => Object.hasOwn(segment, 'route_draft_snapshot')) === true) {
        throw new UnsupportedStructuralMutationError('expanded route draft state requires its durable dataset generation marker');
    }
    for (const segment of mutation.segments) {
        const previous = existingSegments.get(segment.trip_segment_id);
        if (expandedWire &&
            previous !== undefined &&
            Object.hasOwn(previous, 'route_draft_snapshot') &&
            !Object.hasOwn(segment, 'route_draft_snapshot')) {
            throw new UnsupportedStructuralMutationError('an ordinary shift write cannot remove expanded route draft state');
        }
        if (Object.hasOwn(segment, 'route_draft_snapshot')) {
            const routeDraftSnapshot = segment.route_draft_snapshot;
            if (routeDraftSnapshot === undefined) {
                throw new UnsupportedStructuralMutationError('the compatibility reader cannot accept an undefined route draft snapshot');
            }
            if (ROUTE_DRAFT_WIRE_WRITE_MODE === 'compatibility_reader' &&
                !expandedWire &&
                routeDraftSnapshot !== null &&
                !isEmptyRouteDraftSnapshotForProductionA6Rollback(routeDraftSnapshot)) {
                throw new UnsupportedStructuralMutationError('the compatibility reader cannot introduce a route draft snapshot');
            }
            const performanceRecord = segment.status === 'complete' || segment.status === 'partial';
            if ((performanceRecord &&
                segment.route_snapshot !== null &&
                segment.route_draft_snapshot === null) ||
                (!performanceRecord &&
                    segment.route_snapshot === null &&
                    segment.route_draft_snapshot !== null)) {
                continue;
            }
            throw new UnsupportedStructuralMutationError('current segments must use the canonical status-specific route shape');
        }
        if (ROUTE_DRAFT_WIRE_WRITE_MODE === 'compatibility_reader') {
            const performanceRecord = segment.status === 'complete' || segment.status === 'partial';
            if ((performanceRecord && segment.route_snapshot !== null) ||
                (!performanceRecord && segment.route_snapshot === null)) {
                continue;
            }
        }
        if (previous !== undefined &&
            !Object.hasOwn(previous, 'route_draft_snapshot') &&
            sameJson(segment, withoutEntityMetadata(previous))) {
            continue;
        }
        throw new UnsupportedStructuralMutationError('new or changed segments must include the current status-specific route snapshot field');
    }
}
function withoutEntityMetadata(value) {
    const { metadata, ...withoutMetadata } = value;
    void metadata;
    return withoutMetadata;
}
function normalizeUserDefinedLocationMutation(mutation, existing, now) {
    if (mutation.value.origin !== 'user_defined') {
        throw new UnsupportedStructuralMutationError('official catalog locations cannot be written as user-defined locations');
    }
    return locationSchema.parse({
        ...mutation.value,
        metadata: nextExplicitEntityMetadata(existing, now),
    });
}
function normalizeVehicleMutation(mutation, existing, now) {
    return vehicleSchema.parse({
        ...mutation.value,
        metadata: nextExplicitEntityMetadata(existing, now),
    });
}
function normalizeRouteTemplateMutation(mutation, existing, now) {
    return routeTemplateSchema.parse({
        ...mutation.value,
        metadata: nextExplicitEntityMetadata(existing, now),
    });
}
function normalizeRouteVariantMutation(mutation, existing, now, expandedWire) {
    const normalized = routeVariantSchema.parse({
        ...mutation.value,
        metadata: nextExplicitEntityMetadata(existing, now),
    });
    return routeVariantForCurrentWriteMode(normalized, expandedWire);
}
function routeVariantForCurrentWriteMode(input, expandedWire) {
    const value = routeVariantSchema.parse(input);
    if (ROUTE_DRAFT_WIRE_WRITE_MODE !== 'compatibility_reader' || expandedWire)
        return value;
    const rollbackSafe = productionA6RollbackRouteVariantSchema.safeParse(value);
    if (!rollbackSafe.success) {
        throw new ProductionRollbackCompatibilityError();
    }
    return rollbackSafe.data;
}
function nextExplicitEntityMetadata(existing, now) {
    if (existing === null) {
        return { created_at: now, updated_at: now, revision: 1, tombstone: null };
    }
    return {
        created_at: existing.metadata.created_at,
        updated_at: now,
        revision: existing.metadata.revision + 1,
        tombstone: null,
    };
}
function validateShiftDraftBundleCrossLinks(locations, previousLocations, vehicles, routeVariant, routeUsageChange, previousRoute, shiftAggregate, previousShift, targetTripSegmentId) {
    const targetSegment = shiftAggregate.segments.find((segment) => segment.trip_segment_id === targetTripSegmentId);
    if (targetSegment === undefined) {
        throw new UnsupportedStructuralMutationError('the target trip segment is absent from the bundled shift aggregate');
    }
    const previousTarget = previousShift?.segments.find((segment) => segment.trip_segment_id === targetTripSegmentId);
    if (previousTarget !== undefined &&
        previousTarget.metadata.revision === targetSegment.metadata.revision) {
        throw new UnsupportedStructuralMutationError('the target trip segment must be new or changed by the bundle');
    }
    const snapshot = targetSegment.route_snapshot;
    const routeDraftSnapshot = targetSegment.route_draft_snapshot ?? null;
    const legacyRouteDraftShape = ROUTE_DRAFT_WIRE_WRITE_MODE === 'compatibility_reader' &&
        !Object.hasOwn(targetSegment, 'route_draft_snapshot');
    if (snapshot === null) {
        if (routeVariant !== null ||
            routeUsageChange !== null ||
            (targetSegment.status !== 'draft' && targetSegment.status !== 'cancelled') ||
            (routeDraftSnapshot === null && !legacyRouteDraftShape)) {
            throw new UnsupportedStructuralMutationError('draft or cancelled targets require only an unconfirmed route draft snapshot');
        }
    }
    else {
        if ((targetSegment.status !== 'complete' && targetSegment.status !== 'partial') ||
            routeDraftSnapshot !== null ||
            routeVariant === null ||
            routeUsageChange === null ||
            snapshot.route_variant_id !== routeVariant.route_variant_id ||
            !routeSnapshotMatchesVariant(snapshot, routeVariant)) {
            throw new UnsupportedStructuralMutationError('the bundled route variant must exactly match the target segment route snapshot');
        }
        validateRouteUsageChange(routeUsageChange, routeVariant, previousRoute, targetSegment, previousTarget);
    }
    const vehicleSnapshots = vehicles.map(vehicleSnapshotFromEntity);
    if (!sameJson(targetSegment.vehicles, vehicleSnapshots)) {
        throw new UnsupportedStructuralMutationError('the target segment vehicle snapshots must exactly match the bundled vehicles');
    }
    const newLocationIds = new Set(locations
        .filter((_, index) => previousLocations[index] === null)
        .map((location) => location.location_id));
    const newLocationsMatched = new Set();
    const routeLocations = snapshot !== null
        ? [snapshot.origin, ...snapshot.via, snapshot.destination]
        : routeDraftSnapshot === null
            ? []
            : [
                routeDraftEndpointLocationSnapshot(routeDraftSnapshot.origin),
                ...routeDraftSnapshot.via.map(routeDraftEndpointLocationSnapshot),
                routeDraftSnapshot.destination === null
                    ? null
                    : routeDraftEndpointLocationSnapshot(routeDraftSnapshot.destination),
            ].filter((location) => location !== null);
    if (targetSegment.planned_destination !== null &&
        routeLocations.some((candidate) => candidate.location_id === targetSegment.planned_destination?.location_id)) {
        throw new UnsupportedStructuralMutationError('a planned destination must not be captured by the actual route snapshot');
    }
    const snapshotLocations = [
        ...routeLocations,
        ...(targetSegment.origin === null ? [] : [targetSegment.origin]),
        ...(targetSegment.destination === null ? [] : [targetSegment.destination]),
        ...(targetSegment.planned_destination === null ? [] : [targetSegment.planned_destination]),
    ];
    for (const location of locations) {
        const matchingSnapshots = snapshotLocations.filter((candidate) => candidate.location_id === location.location_id);
        if (matchingSnapshots.length === 0) {
            continue;
        }
        if (!matchingSnapshots.every((candidate) => locationMatchesSnapshot(location, candidate))) {
            throw new UnsupportedStructuralMutationError(`bundled location ${location.location_id} does not match the target segment snapshot`);
        }
        newLocationsMatched.add(location.location_id);
    }
    for (const locationId of newLocationIds) {
        if (!newLocationsMatched.has(locationId)) {
            throw new UnsupportedStructuralMutationError(`new bundled location ${locationId} is not captured by the changed target segment`);
        }
    }
}
function validateRouteUsageChange(routeUsageChange, routeVariant, previousRoute, targetSegment, previousTarget) {
    const snapshot = targetSegment.route_snapshot;
    if (snapshot === null) {
        throw new UnsupportedStructuralMutationError('the target trip segment must capture the bundled route variant');
    }
    const previousSnapshot = previousTarget?.route_snapshot ?? null;
    if (routeUsageChange === 'record_new_use') {
        if (previousSnapshot?.route_variant_id === routeVariant.route_variant_id ||
            previousSnapshot?.confirmation.route_fingerprint_sha256 ===
                routeVariant.confirmation?.route_fingerprint_sha256) {
            throw new UnsupportedStructuralMutationError('editing the same existing route must preserve its usage counters');
        }
        if (routeVariant.last_used_at !== snapshot.captured_at) {
            throw new UnsupportedStructuralMutationError('a new route use must set last_used_at to the target snapshot captured_at');
        }
        const expectedUseCount = (previousRoute?.use_count ?? 0) + 1;
        const expectedConfirmationCount = (previousRoute?.explicit_confirmation_count ?? 0) + 1;
        if (routeVariant.use_count !== expectedUseCount ||
            routeVariant.explicit_confirmation_count !== expectedConfirmationCount) {
            throw new UnsupportedStructuralMutationError('a new route use must increment use and explicit confirmation counters exactly once');
        }
        return;
    }
    if (routeUsageChange === 'materialize_legacy_existing_use') {
        if (previousRoute !== null ||
            previousSnapshot === null ||
            previousSnapshot.route_variant_id !== routeVariant.route_variant_id ||
            previousSnapshot.confirmation.route_fingerprint_sha256 !==
                routeVariant.confirmation?.route_fingerprint_sha256) {
            throw new UnsupportedStructuralMutationError('legacy route materialization requires the exact missing identity and prior snapshot');
        }
        return;
    }
    if (previousRoute === null ||
        previousSnapshot === null ||
        previousSnapshot.route_variant_id !== routeVariant.route_variant_id ||
        previousSnapshot.confirmation.route_fingerprint_sha256 !==
            routeVariant.confirmation?.route_fingerprint_sha256 ||
        previousRoute.confirmation?.route_fingerprint_sha256 !==
            routeVariant.confirmation?.route_fingerprint_sha256) {
        throw new UnsupportedStructuralMutationError('route usage can be preserved only when editing the same existing route');
    }
    if (routeVariant.use_count !== previousRoute.use_count ||
        routeVariant.explicit_confirmation_count !== previousRoute.explicit_confirmation_count ||
        routeVariant.last_used_at !== previousRoute.last_used_at) {
        throw new UnsupportedStructuralMutationError('editing the same existing route must preserve use counters and last_used_at exactly');
    }
}
function changedRouteIds(previousAggregate, aggregate) {
    const previousSegments = new Map(previousAggregate?.segments.map((segment) => [segment.trip_segment_id, segment]) ?? []);
    return aggregate.segments
        .flatMap((segment) => {
        const previous = previousSegments.get(segment.trip_segment_id);
        if (previous?.metadata.revision === segment.metadata.revision)
            return [];
        return [
            previous?.route_snapshot?.route_variant_id ?? null,
            segment.route_snapshot?.route_variant_id ?? null,
        ];
    })
        .filter((routeId) => routeId !== null)
        .filter((routeId, index, routeIds) => routeIds.indexOf(routeId) === index)
        .sort();
}
function routeVariantFingerprint(routeVariant) {
    return routeFingerprintSha256({
        candidate_source: routeVariant.candidate_source,
        destination_location_id: routeVariant.destination_location_id,
        graph_sha256: routeVariant.graph_version?.sha256 ?? null,
        network_edge_ids: routeVariant.network_segments.map((segment) => segment.graph_edge_id),
        origin_location_id: routeVariant.origin_location_id,
        via_location_ids: routeVariant.via_location_ids,
    });
}
function assertRouteTemplateDraftBinding(draft, template) {
    const source = template.source_snapshot;
    const selection = draft.route_selection;
    if (source === undefined ||
        selection?.kind !== 'offer' ||
        selection.offer_source !== 'template' ||
        selection.offer_id !== `template:${template.route_template_id}`) {
        throw new UnsupportedStructuralMutationError('template application requires the exact durable template offer selection');
    }
    const normalizedSourceSeed = normalizeRouteSeedForCurrentScoring(source.route_seed);
    if (draft.route_seed === null ||
        !sameJson(draft.route_seed, normalizedSourceSeed) ||
        draft.origin.carried_location_snapshot === null ||
        !sameJson(draft.origin.carried_location_snapshot, source.origin) ||
        draft.destination === null ||
        draft.destination.carried_location_snapshot === null ||
        !sameJson(draft.destination.carried_location_snapshot, source.destination) ||
        draft.via.length !== normalizedSourceSeed.via.length ||
        draft.via.some((endpoint, index) => {
            const sourceVia = normalizedSourceSeed.via[index];
            return (sourceVia === undefined ||
                endpoint.carried_location_snapshot === null ||
                !sameJson(endpoint.carried_location_snapshot, sourceVia));
        })) {
        throw new UnsupportedStructuralMutationError('template application draft route seed and endpoints do not match its source snapshot');
    }
    const expectedTrain = template.default_train_or_service_number ?? '';
    const expectedWorkType = canonicalRouteTemplateWorkType(template.default_work_type);
    const expectedOperator = template.default_operator ?? '';
    if (draft.train_or_service_number !== expectedTrain ||
        draft.work_type !== expectedWorkType ||
        draft.operator !== expectedOperator ||
        draft.vehicles.length !== template.default_vehicle_ids.length ||
        draft.vehicles.some((vehicle, index) => vehicle.vehicle_id !== template.default_vehicle_ids[index] ||
            !sameJson({
                vehicle_id: vehicle.vehicle_id,
                series: vehicle.series,
                full_number: vehicle.full_number,
                nickname: vehicle.nickname,
                operator: vehicle.operator,
                vehicle_type: vehicle.vehicle_type,
            }, {
                vehicle_id: source.vehicles.find((candidate) => candidate.vehicle_id === vehicle.vehicle_id)?.vehicle_id,
                series: source.vehicles.find((candidate) => candidate.vehicle_id === vehicle.vehicle_id)
                    ?.series,
                full_number: source.vehicles.find((candidate) => candidate.vehicle_id === vehicle.vehicle_id)
                    ?.full_number ?? '',
                nickname: source.vehicles.find((candidate) => candidate.vehicle_id === vehicle.vehicle_id)
                    ?.nickname ?? '',
                operator: source.vehicles.find((candidate) => candidate.vehicle_id === vehicle.vehicle_id)
                    ?.operator ?? '',
                vehicle_type: source.vehicles.find((candidate) => candidate.vehicle_id === vehicle.vehicle_id)?.vehicle_type,
            }))) {
        throw new UnsupportedStructuralMutationError('template application draft defaults do not match the current template');
    }
}
function segmentChangeKind(before, after, fallback) {
    void fallback;
    if (before === null)
        return 'create';
    const beforeDeleted = before.metadata.tombstone !== null;
    const afterDeleted = after.metadata.tombstone !== null;
    if (!beforeDeleted && afterDeleted)
        return 'soft_delete';
    if (beforeDeleted && !afterDeleted)
        return 'restore';
    return 'edit';
}
async function collectShiftAggregatesReplacing(database, datasetEpoch, replacement) {
    const rows = await database.entities.where('entity_type').equals('shift_aggregate').toArray();
    const aggregates = [];
    let replaced = false;
    for (const row of rows) {
        assertRowEpoch(row, datasetEpoch);
        if (row.payload.entity_type !== 'shift_aggregate') {
            throw new Error('shift aggregate index returned another entity type');
        }
        if (row.entity_id === replacement.shift.shift_id) {
            aggregates.push(replacement);
            replaced = true;
        }
        else {
            aggregates.push(shiftAggregateSchema.parse(row.payload.value));
        }
    }
    if (!replaced)
        aggregates.push(replacement);
    return aggregates;
}
async function findLiveRouteVariant(database, datasetEpoch, routeId) {
    const row = await database.entities.get(entityTableKey('route_variant', routeId));
    if (row === undefined)
        return null;
    assertRowEpoch(row, datasetEpoch);
    if (row.payload.entity_type !== 'route_variant') {
        throw new Error('route variant key returned another entity type');
    }
    const route = routeVariantSchema.parse(row.payload.value);
    if (route.metadata.tombstone !== null) {
        throw new UnsupportedStructuralMutationError('live route learning cannot be recomputed from a soft-deleted route variant');
    }
    return route;
}
async function recomputeChangedLiveRoutes(database, datasetEpoch, routeIds, aggregates, now, expandedWire) {
    assertShiftAggregatesUseAuthorizedWire(aggregates, expandedWire);
    const changes = [];
    for (const routeId of routeIds) {
        const previous = await findLiveRouteVariant(database, datasetEpoch, routeId);
        // Aggregate-only legacy writes may contain snapshots without learned route entities.
        if (previous === null)
            continue;
        const candidate = recomputeRouteLearning(previous, aggregates, now, previous, expandedWire);
        if (routeVariantLearningMatches(previous, candidate))
            continue;
        changes.push({ previous, value: candidate });
    }
    return changes;
}
function assertShiftAggregatesUseAuthorizedWire(aggregates, expandedWire) {
    if (!expandedWire &&
        aggregates.some((aggregate) => aggregate.segments.some((segment) => Object.hasOwn(segment, 'route_draft_snapshot')))) {
        throw new UnsupportedStructuralMutationError('expanded route draft state requires its durable dataset generation marker');
    }
}
function routeVariantLearningMatches(left, right) {
    const { metadata: leftMetadata, ...leftValue } = left;
    const { metadata: rightMetadata, ...rightValue } = right;
    void leftMetadata;
    void rightMetadata;
    return sameJson(leftValue, rightValue);
}
function recomputeRouteLearning(previous, aggregates, now, revisionBase = previous, expandedWire = false) {
    const fingerprint = routeVariantFingerprint(previous);
    const uses = [];
    for (const aggregate of aggregates) {
        if (aggregate.shift.metadata.tombstone !== null) {
            continue;
        }
        for (const segment of aggregate.segments) {
            const snapshot = segment.route_snapshot;
            if (segment.metadata.tombstone !== null ||
                snapshot === null ||
                (segment.status !== 'complete' && segment.status !== 'partial') ||
                snapshot.route_variant_id !== previous.route_variant_id) {
                continue;
            }
            if (snapshot.confirmation.route_fingerprint_sha256 !== fingerprint) {
                throw new UnsupportedStructuralMutationError('a live route snapshot does not match its learned route semantic identity');
            }
            uses.push({
                service_date: aggregate.shift.service_date,
                shift_id: aggregate.shift.shift_id,
                segment,
                snapshot,
            });
        }
    }
    uses.sort((left, right) => compareRouteUseOrder({
        service_date: left.service_date,
        actual_start: left.segment.actual_start?.instant ?? null,
        shift_id: left.shift_id,
        segment_order: left.segment.order,
        trip_segment_id: left.segment.trip_segment_id,
    }, {
        service_date: right.service_date,
        actual_start: right.segment.actual_start?.instant ?? null,
        shift_id: right.shift_id,
        segment_order: right.segment.order,
        trip_segment_id: right.segment.trip_segment_id,
    }));
    const latest = uses.at(-1);
    return routeVariantForCurrentWriteMode(routeVariantSchema.parse({
        ...previous,
        ...(latest === undefined
            ? {}
            : {
                origin_location_id: latest.snapshot.origin.location_id,
                destination_location_id: latest.snapshot.destination.location_id,
                via_location_ids: latest.snapshot.via.map((location) => location.location_id),
                network_segments: latest.snapshot.network_segments,
                distance: latest.snapshot.distance,
                candidate_source: latest.snapshot.candidate_source,
                catalog_version: latest.snapshot.catalog_version,
                graph_version: latest.snapshot.graph_version,
                route_scoring_version: latest.snapshot.route_scoring_version,
            }),
        confirmation: latest?.snapshot.confirmation ?? null,
        explicit_confirmation_count: uses.length,
        use_count: uses.length,
        last_used_at: latest?.snapshot.captured_at ?? null,
        favorite: revisionBase?.favorite ?? previous.favorite,
        // Explicit preferences survive rebuilds, undo, delete and restore. The
        // separate three-confirmation rule still gates automatic preselection.
        default_for_context: revisionBase?.default_for_context ?? previous.default_for_context,
        usage_contexts: recomputeRouteUsageContexts(uses),
        metadata: nextExplicitEntityMetadata(revisionBase, now),
    }), expandedWire);
}
function recomputeRouteUsageContexts(uses) {
    const contextsByIdentity = new Map();
    for (const { segment, snapshot } of uses) {
        const vehicleIds = segment.vehicles.length === 0
            ? [null]
            : segment.vehicles.map((vehicle) => vehicle.vehicle_id).sort();
        for (const vehicleId of vehicleIds) {
            const context = {
                direction_key: `${snapshot.origin.location_id}>${snapshot.destination.location_id}`,
                train_or_service_number: segment.train_or_service_number,
                work_type: segment.work_type,
                operator: segment.operator,
                vehicle_id: vehicleId,
            };
            const identity = canonicalJsonStringify(context);
            contextsByIdentity.delete(identity);
            contextsByIdentity.set(identity, context);
        }
    }
    return [...contextsByIdentity.values()].slice(-512);
}
function vehicleSnapshotFromEntity(vehicle) {
    return {
        vehicle_id: vehicle.vehicle_id,
        series: vehicle.series,
        full_number: vehicle.full_number,
        nickname: vehicle.nickname,
        operator: vehicle.operator,
        vehicle_type: vehicle.vehicle_type,
    };
}
function routeSnapshotMatchesVariant(snapshot, routeVariant) {
    return sameJson({
        route_variant_id: snapshot.route_variant_id,
        origin_location_id: snapshot.origin.location_id,
        via_location_ids: snapshot.via.map((location) => location.location_id),
        destination_location_id: snapshot.destination.location_id,
        network_segments: snapshot.network_segments,
        distance: snapshot.distance,
        candidate_source: snapshot.candidate_source,
        confirmation: snapshot.confirmation,
        catalog_version: snapshot.catalog_version,
        graph_version: snapshot.graph_version,
        route_scoring_version: snapshot.route_scoring_version,
    }, {
        route_variant_id: routeVariant.route_variant_id,
        origin_location_id: routeVariant.origin_location_id,
        via_location_ids: routeVariant.via_location_ids,
        destination_location_id: routeVariant.destination_location_id,
        network_segments: routeVariant.network_segments,
        distance: routeVariant.distance,
        candidate_source: routeVariant.candidate_source,
        confirmation: routeVariant.confirmation,
        catalog_version: routeVariant.catalog_version,
        graph_version: routeVariant.graph_version,
        route_scoring_version: routeVariant.route_scoring_version,
    });
}
function locationMatchesSnapshot(location, snapshot) {
    if (snapshot === null) {
        return false;
    }
    const coreMatches = sameJson({
        location_id: location.location_id,
        display_name: location.official_name,
        location_type: location.location_type,
        country_code: location.country_code,
    }, {
        location_id: snapshot.location_id,
        display_name: snapshot.display_name,
        location_type: snapshot.location_type,
        country_code: snapshot.country_code,
    });
    return (coreMatches &&
        snapshot.key_identifiers.every((keyIdentifier) => location.identifiers.some((identifier) => identifier.namespace === keyIdentifier.namespace &&
            identifier.namespace_other === keyIdentifier.namespace_other &&
            identifier.value === keyIdentifier.value)));
}
function makePendingOperation(input) {
    const row = entityRowFromPayload(input.payload, input.dataset_epoch);
    return parsePendingOperation({
        contract_version: 'sync_operation.v1',
        operation_id: input.operation_id,
        idempotency_key_sha256: sha256HexUtf8(`dmf.sync-operation.idempotency.v1\u0000${input.operation_id}`),
        device_id: input.device_id,
        local_profile_id: input.local_profile_id,
        dataset_epoch: input.dataset_epoch,
        local_sequence: input.local_sequence,
        operation_kind: input.operation_kind ?? 'upsert',
        operation_origin: input.operation_origin,
        entity_id: row.entity_id,
        base_revision: input.base_revision,
        resulting_revision: row.revision,
        payload: input.payload,
        created_at: input.created_at,
        server_cursor: null,
    });
}
function nextMetadata(existing, incomingWithoutMetadata, now) {
    if (existing === null) {
        return { created_at: now, updated_at: now, revision: 1, tombstone: null };
    }
    const existingWithoutMetadata = { ...existing };
    delete existingWithoutMetadata.metadata;
    if (sameJson(existingWithoutMetadata, incomingWithoutMetadata)) {
        return existing.metadata;
    }
    return {
        created_at: existing.metadata.created_at,
        updated_at: now,
        revision: existing.metadata.revision + 1,
        tombstone: null,
    };
}
function incrementDeletedMetadata(metadata, now, tombstone) {
    return {
        created_at: metadata.created_at,
        updated_at: now,
        revision: metadata.revision + 1,
        tombstone,
    };
}
function incrementLiveMetadata(metadata, now) {
    if (metadata.tombstone !== null) {
        throw new UnsupportedStructuralMutationError('live metadata cannot be derived from a tombstoned record');
    }
    return {
        created_at: metadata.created_at,
        updated_at: now,
        revision: metadata.revision + 1,
        tombstone: null,
    };
}
function incrementRestoredMetadata(metadata, now) {
    return {
        created_at: metadata.created_at,
        updated_at: now,
        revision: metadata.revision + 1,
        tombstone: null,
    };
}
function rootTombstone(payload) {
    switch (payload.entity_type) {
        case 'shift_aggregate':
            return payload.value.shift.metadata.tombstone;
        case 'vehicle':
        case 'route_variant':
        case 'route_template':
        case 'user_defined_location':
        case 'location_merge_decision':
        case 'user_preferences':
            return payload.value.metadata.tombstone;
    }
}
function assignDeletionCursor(payload, deletionCursor) {
    if (payload.entity_type !== 'shift_aggregate') {
        throw new UnsupportedStructuralMutationError('generic entity deletion acknowledgement is not implemented in Phase 2');
    }
    const aggregate = payload.value;
    const tombstone = aggregate.shift.metadata.tombstone;
    if (tombstone === null) {
        throw new Error('soft-delete acknowledgement requires a durable tombstone');
    }
    const updatedTombstone = { ...tombstone, deletion_cursor: deletionCursor };
    return {
        entity_type: 'shift_aggregate',
        value: shiftAggregateSchema.parse({
            ...aggregate,
            shift: {
                ...aggregate.shift,
                metadata: { ...aggregate.shift.metadata, tombstone: updatedTombstone },
            },
            segments: aggregate.segments.map((segment) => ({
                ...segment,
                metadata: { ...segment.metadata, tombstone: updatedTombstone },
            })),
        }),
    };
}
function operationStillOwnsTombstone(operation, row) {
    if (operation.operation_kind !== 'soft_delete' ||
        row.dataset_epoch !== operation.dataset_epoch ||
        row.revision !== operation.resulting_revision ||
        row.entity_type !== operation.payload.entity_type ||
        row.entity_id !== operation.entity_id) {
        return false;
    }
    const current = rootTombstone(row.payload);
    const acknowledged = rootTombstone(operation.payload);
    return (current !== null &&
        acknowledged !== null &&
        current.deletion_operation_id === operation.operation_id &&
        current.deletion_operation_id === acknowledged.deletion_operation_id &&
        current.dataset_epoch === operation.dataset_epoch &&
        current.deleted_at === acknowledged.deleted_at &&
        current.restore_until === acknowledged.restore_until);
}
async function assignShiftDeletionCursorToLatestTripRevisions(database, aggregate, operation, deletionCursor) {
    const revisions = (await database.trip_revisions.where('shift_id').equals(aggregate.shift.shift_id).toArray()).map((revision) => tripRevisionSchema.parse(revision));
    const latestBySegment = new Map();
    for (const revision of revisions) {
        if (revision.shift_id !== aggregate.shift.shift_id ||
            revision.dataset_epoch !== aggregate.dataset_epoch) {
            throw new UnsupportedStructuralMutationError('trip revision ownership does not match acknowledged shift deletion');
        }
        const current = latestBySegment.get(revision.trip_segment_id);
        if (current === undefined ||
            revision.resulting_revision > current.resulting_revision ||
            (revision.resulting_revision === current.resulting_revision &&
                (compareInstants(revision.recorded_at, current.recorded_at) > 0 ||
                    (revision.recorded_at === current.recorded_at &&
                        revision.trip_revision_id.localeCompare(current.trip_revision_id) > 0)))) {
            latestBySegment.set(revision.trip_segment_id, revision);
        }
    }
    const updated = [];
    for (const segment of aggregate.segments) {
        const revision = latestBySegment.get(segment.trip_segment_id);
        if (revision === undefined ||
            revision.after === null ||
            revision.resulting_revision !== segment.metadata.revision ||
            !sameSegmentIgnoringDeletionCursor(revision.after, segment)) {
            throw new UnsupportedStructuralMutationError('acknowledged shift deletion lacks its exact latest trip revision');
        }
        const tombstone = revision.after.metadata.tombstone;
        if (tombstone === null ||
            tombstone.deletion_operation_id !== operation.operation_id ||
            tombstone.dataset_epoch !== operation.dataset_epoch) {
            throw new UnsupportedStructuralMutationError('latest trip revision is not owned by the acknowledged shift deletion');
        }
        updated.push(tripRevisionSchema.parse({
            ...revision,
            after: {
                ...revision.after,
                metadata: {
                    ...revision.after.metadata,
                    tombstone: { ...tombstone, deletion_cursor: deletionCursor },
                },
            },
        }));
    }
    if (updated.length > 0)
        await database.trip_revisions.bulkPut(updated);
}
function sameSegmentIgnoringDeletionCursor(left, right) {
    const normalize = (segment) => ({
        ...segment,
        metadata: {
            ...segment.metadata,
            tombstone: segment.metadata.tombstone === null
                ? null
                : { ...segment.metadata.tombstone, deletion_cursor: null },
        },
    });
    return sameJson(normalize(left), normalize(right));
}
function parseDraftKey(input) {
    if (input.length === 0 || input.length > 256 || input.trim() !== input) {
        throw new Error('draft_key must be canonical non-empty text up to 256 characters');
    }
    return input;
}
function parseDraftVersionToken(input) {
    if (!Number.isSafeInteger(input.autosave_revision) || input.autosave_revision < 1) {
        throw new Error('draft autosave revision must be a positive safe integer');
    }
    return {
        draft_id: uuidSchema.parse(input.draft_id),
        autosave_revision: input.autosave_revision,
    };
}
function persistedShiftEditorDraftPayload(input) {
    const draft = shiftEditorDraftSchema.parse(input);
    const expandedPayload = jsonValueSchema.parse(draft);
    if (ROUTE_DRAFT_WIRE_WRITE_MODE === 'expanded_writer') {
        return { payload: expandedPayload };
    }
    return {
        payload: jsonValueSchema.parse(projectShiftEditorDraftForV3Rollback(draft)),
        expanded_payload: expandedPayload,
    };
}
function persistedShiftEditorDraftPayloadFromUnknown(input) {
    try {
        return persistedShiftEditorDraftPayload(parseShiftEditorDraft(input));
    }
    catch {
        // The repository historically accepts opaque shift-editor JSON and leaves
        // semantic validation to its caller. Preserve that boundary for tooling
        // and corrupted-record tests; AppStorage always supplies the typed wire.
        return { payload: jsonValueSchema.parse(input) };
    }
}
function draftRecordForCurrentReader(input) {
    const { expanded_payload: expandedPayload, ...rollbackRecord } = input;
    return structuredClone({
        ...rollbackRecord,
        payload: expandedPayload ?? rollbackRecord.payload,
    });
}
function draftRecordWithoutExpandedPayload(input) {
    const { expanded_payload: expandedPayload, ...rollbackRecord } = input;
    void expandedPayload;
    return rollbackRecord;
}
function draftVersion(draft) {
    return {
        draft_id: draft.draft_id,
        autosave_revision: draft.autosave_revision,
    };
}
function sameJson(left, right) {
    return (canonicalJsonStringify(left) ===
        canonicalJsonStringify(right));
}
function replacementCheckpointSha256(snapshot) {
    return sha256HexUtf8(`dmf.local-replacement-checkpoint.sha256.v1\u0000${canonicalJsonStringify(snapshot)}`);
}
function replacementPayloadSha256(rows, tripRevisions, routeDraftWireGeneration) {
    const sortedRows = [...rows].sort((left, right) => `${left.entity_type}\u0000${left.entity_id}`.localeCompare(`${right.entity_type}\u0000${right.entity_id}`));
    const sortedTripRevisions = [...tripRevisions].sort((left, right) => left.trip_revision_id.localeCompare(right.trip_revision_id));
    return sha256HexUtf8(`dmf.authoritative-replacement-payload.sha256.v2\u0000${canonicalJsonStringify({
        entities: sortedRows,
        route_draft_wire_generation: routeDraftWireGeneration,
        trip_revisions: sortedTripRevisions,
    })}`);
}
function assertRowEpoch(row, expectedEpoch) {
    if (row.dataset_epoch !== expectedEpoch) {
        throw new DatasetEpochMismatchError(expectedEpoch, row.dataset_epoch);
    }
    const canonical = entityRowFromPayload(row.payload, expectedEpoch);
    if (canonical.entity_type !== row.entity_type ||
        canonical.entity_id !== row.entity_id ||
        canonical.revision !== row.revision ||
        canonical.updated_at !== row.updated_at ||
        canonical.tombstone_deleted_at !== row.tombstone_deleted_at ||
        canonical.compound_key[0] !== row.compound_key[0] ||
        canonical.compound_key[1] !== row.compound_key[1]) {
        throw new UnsupportedStructuralMutationError('entity storage key or envelope does not match its canonical payload');
    }
}
//# sourceMappingURL=repository.js.map